/**
 * Copy de las cuatro páginas de Producto del rediseño V3.
 *
 * Se cuelga de `landingV3.content.js` igual que `landingV3.home.js`. Van las
 * cuatro juntas porque comparten esqueleto (hero → problema → secciones →
 * «por qué importa» → FAQ → cierre) y se leen mejor una al lado de otra que
 * en cuatro ficheros de 120 líneas.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CINCO COSAS DEL DISEÑO QUE NO SE COPIAN LITERALES, Y POR QUÉ
 * ────────────────────────────────────────────────────────────────────────────
 *
 * El diseño describe el producto con mucha precisión —las seis categorías del
 * Análisis SEO y sus topes, la escalera de niveles, el retardo por rango, los
 * seis botones de publicación— y todo eso se ha contrastado contra el código y
 * encaja. Cinco afirmaciones NO encajaban:
 *
 * 1. **Publicaciones que se repiten cada semana o cada mes.** NO EXISTE. Un
 *    programado es de un solo disparo (`scheduled_post_repo.create`: un
 *    `scheduled_at` y un `status` que va de `pending` a `published`). Estaba en
 *    el cuerpo y en una FAQ; se ha quitado de los dos sitios. Si algún día se
 *    construye, este es el copy que ya estaba escrito para él.
 * 2. **«Mejor que el 68% de los locales en tu zona».** NO EXISTE ningún
 *    percentil ni comparación con la zona en el Análisis SEO. Se ha quitado el
 *    chip entero.
 * 3. **«Las palabras clave que necesites, no hay límite».** El tope real es de
 *    20 por regla (`MAX_KEYWORDS_PER_RULE`) y el serializer responde 400 al
 *    pasarse. Ahora la FAQ dice 20.
 * 4. **«Nunca una plantilla repetida».** Tampoco ahora, aunque el modo
 *    estándar ya no se pueda elegir y se responda siempre con IA: si se agota
 *    el cupo horario o falla OpenAI, el motor publica un texto fijo como
 *    paracaídas, que es literalmente una plantilla. El copy dice que responde
 *    la IA, pero no promete que nunca salga un texto repetido.
 * 5. **«Sin ningún dominio añadido no se muestra en ninguna parte».** Sin
 *    dominios el `frame-ancestors` se queda en `'self'`, así que el widget
 *    SIGUE viéndose dentro de nuestro propio panel — que es justo lo que hace
 *    funcionar la vista previa. Lo que es cierto es que no se ve en ninguna web
 *    ajena, y eso es lo que dice ahora.
 *
 * Las dos cifras de terceros («40-50% de los clics se los lleva el pack local»,
 * «el 88% confía en una reseña casi tanto como en un amigo», «9-15% de
 * conversión») vienen del diseño y NO se han podido verificar aquí: son
 * estudios externos, no datos nuestros. Se mantienen porque son la clase de
 * cifra que el equipo de marketing sostiene, pero conviene tener la fuente a
 * mano antes de que esto salga de `/v3`.
 */

/*
 * La geometría de la captura del Mapa SEO, sin el `alt` —que sí se traduce—.
 * Es la misma imagen que abre Multiubicaciones: vive en la carpeta de aquella
 * página porque es de donde salió, pero es una captura del Mapa SEO, no de
 * nada propio de Multiubicaciones, y ahora la usan las dos.
 */
const CAPTURA_MAPA = {
  src: '/landing-images/v3/multiubicaciones/mapa-seo-grupo.webp',
  ancho: 1224,
  alto: 828,
};

const visibilidadSeo = {
  hero: {
    badge: '¿Sabes en qué posición sales cuando tus clientes te buscan?',
    titulo: 'Domina tu zona en ',
    acento: 'Google Maps',
    sub: 'Atrae más clientes apareciendo en mejor posición, no solo en la puerta de tu negocio.',
    cta: 'Probar 7 días gratis',
    ctaVer: 'Ver el escáner en acción',
    consulta: '«restaurante sushi cerca de mí»',
    etiquetaZona: 'Tu zona',
  },

  problema: {
    pinCerca: 'En tu calle',
    pinLejos: 'A 800 m',
    titulo: 'Estar en Google Maps no es lo mismo que aparecer en Google Maps',
    p1: 'Si buscas tu propio negocio, o buscas estando en tu local, casi siempre vas a aparecer bien. Pero un cliente real no te busca por nombre: busca algo como «restaurante sushi cerca de mí» desde otro punto de tu zona, sin saber que existes todavía.',
    p2: 'Ahí es donde tu posición cambia. Puedes ser el primero en tu calle y no aparecer ni en la segunda página a unos cientos de metros. Es un punto ciego que casi nadie detecta, porque la forma normal de comprobarlo —buscarte a ti mismo— es justo la que nunca lo revela.',
  },

  escaneo: {
    kicker: 'Escaneo de posición',
    titulo: 'Así te ve Google desde cada punto de tu zona',
    cuerpo: 'Eliges qué buscan tus clientes (por ejemplo, «restaurante sushi» o «sushi cerca de mí») y el tamaño de la zona alrededor de tu negocio. En segundos tienes un mapa con tu posición exacta en cada punto: verde si sales entre los primeros, rojo si te cuesta aparecer, gris si directamente no sales.',
    captura: {
      ...CAPTURA_MAPA,
      alt: 'El Mapa SEO del panel: a la izquierda el formulario de escaneo con el local elegido y los resultados, a la derecha la cuadrícula de posiciones sobre el mapa de la ciudad.',
    },
    // Las tres cifras son las MISMAS que se ven en la captura de al lado. Con
    // números distintos, cualquiera que mire las dos cosas a la vez ve que uno
    // de los dos está inventado —y lo estaba—.
    kpis: [
      {
        clave: 'media',
        l: 'Posición media',
        v: '7,9',
        d: 'Tu puesto medio, contando solo los puntos donde apareces',
      },
      {
        clave: 'cobertura',
        l: 'Cobertura',
        v: '89,8 %',
        d: 'En cuántos de los puntos escaneados sales, aunque sea en un puesto bajo',
      },
      {
        clave: 'dominancia',
        l: 'Dominancia local',
        v: '20,4 %',
        d: 'En qué porcentaje de puntos estás entre los 3 primeros',
      },
    ],
  },

  /** Lo propio de esta página en el comparador; el resto de etiquetas —las tres
   *  vistas, la guía— se heredan de las de la home, que son las mismas. */
  competidores: {
    kicker: 'Competidores',
    titulo: 'Compárate con quien de verdad compite por tus clientes',
    cuerpo: 'Eliges al rival por su nombre: el mismo negocio que le sale a tus clientes cuando buscan lo que tú vendes. Lo escoges de la lista de negocios de tu zona, con su valoración y sus reseñas a la vista, y comparas su posición con la tuya, punto por punto. Así ves algo que un promedio nunca te diría: puede que en tu calle vayas bien, pero a unos cientos de metros él aparezca antes que tú.',
  },

  analisis: {
    kicker: 'Análisis SEO',
    titulo: 'Sabes exactamente qué te está frenando, no solo que vas mal',
    p1: 'Tu ficha recibe una puntuación de 0 a 100 que resume qué tan preparada está para posicionarse bien, con niveles que van desde «Empezando» hasta «Líder local».',
    p2: 'Pero el número en sí no es lo que cambia las cosas. Lo que importa es que detrás de esa puntuación sabemos exactamente qué está pesando: si te faltan reseñas recientes, si tu ficha lleva meses sin publicar nada, si las categorías no encajan con lo que la gente busca, o si simplemente te faltan fotos actualizadas.',
    p3: 'No es una opinión ni una caja negra: sale de los datos reales de tu ficha. Por eso, cuando cambias algo, la puntuación se mueve de verdad, no según lo que digamos nosotros.',
    p4: 'Y en vez de dejarte con una lista de veinte cosas por revisar, te decimos cuál es la que más te va a mover primero. Así empiezas por ahí, no por donde te parezca a ti.',

    tarjeta: {
      puntuacion: '62',
      sobre: '/ 100',
      nivelEtiqueta: 'Nivel actual',
      nivel: 'Camino al podio',
      // El siguiente peldaño real de la escalera (`healthScore.level.*`).
      faltan: 'Te quedan 9 puntos para llegar a «Bien posicionado».',
      metricas: [
        { clave: 'sinResponder', l: 'Reseñas sin responder', v: '4', tono: 'mal' },
        { clave: 'tasa', l: 'Tasa de respuesta (30 días)', v: '62 % · objetivo 80 %', tono: 'medio' },
        { clave: 'evolucion', l: 'Evolución este mes', v: '+6 pts', tono: 'bien' },
      ],
      mision: {
        etiqueta: 'Próxima misión',
        texto: 'Responde las reseñas en menos de 24 horas',
        puntos: '+8 pts al completar',
      },
      // Las seis del `health_score.py`, con sus topes reales (suman 100).
      categorias: [
        { clave: 'nap', n: 'Ficha NAP', p: 20, t: 20 },
        { clave: 'cat', n: 'Categorías', p: 16, t: 20 },
        { clave: 'vis', n: 'Presencia visual', p: 12, t: 18 },
        { clave: 'rep', n: 'Reputación', p: 8, t: 20 },
        { clave: 'srv', n: 'Keywords y servicios', p: 6, t: 12 },
        { clave: 'act', n: 'Actividad', p: 0, t: 10 },
      ],
    },
  },

  porQue: {
    kicker: 'Por qué importa',
    cifra: '40-50 %',
    cifraPie: 'de los clics de la búsqueda se los lleva el mapa de los tres primeros',
    titulo: 'El primer resultado no se lleva solo el primer clic, se lleva la mitad',
    p1: 'Cuando alguien busca un negocio como el tuyo, casi nunca llega a abrir una web. Ve el mapa con los tres primeros resultados (el «pack local») y decide ahí mismo.',
    p2: 'Ese mapa se lleva entre el 40 y el 50 % de todos los clics de la búsqueda. Si apareces en el puesto 4 o más abajo, te queda una fracción mínima de ese tráfico, aunque tu negocio sea mejor que los tres de arriba.',
    p3: 'No es un margen pequeño que se pueda ignorar. Es la diferencia entre que te encuentren o que ni te vean.',
  },

  faqTitulo: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Necesito conocimientos técnicos para usarlo?',
      a: 'No. Conectas tu ficha de Google Business en dos minutos y tienes tu primer escaneo en la misma sesión.',
    },
    {
      q: '¿Cuántos puntos escanea y de qué depende?',
      // No se remite a Precios: esa sección no dice nada de créditos, y mandar
      // al visitante a buscar algo que no está es peor que no mencionarlo.
      a: 'Eliges el tamaño de la cuadrícula alrededor de tu negocio, desde una zona pequeña hasta una cobertura amplia. Cuanta más zona cubras, más puntos escaneas: una de 5×5 son 25 puntos y una de 7×7 son 49. Cada punto cuesta un crédito, y el saldo que te queda lo tienes siempre a la vista en la propia pantalla del escáner.',
    },
    {
      q: '¿Puedo comparar con más de un competidor a la vez?',
      a: 'Ves la lista completa de negocios de tu categoría en la zona, con su posición media y su dominancia local. Comparas contra uno a la vez, pero puedes cambiar de competidor cuando quieras sin gastar un escaneo nuevo.',
    },
    {
      q: 'Tengo varios locales, ¿funciona igual?',
      a: 'Sí. Cada local tiene su propia cuadrícula y su propia puntuación, y puedes ver un ranking comparativo entre todos tus locales para saber cuál necesita más atención.',
    },
    {
      q: '¿La puntuación del Análisis SEO se actualiza sola o hay que volver a escanear?',
      // Se recalcula al abrir la pantalla, pero sobre lo que tenemos cacheado
      // de Google, que se refresca por TTL. Decir «al instante» sería vender
      // una inmediatez que no hay.
      a: 'Se actualiza sola. No está ligada a los escaneos del Mapa SEO, así que no gasta créditos ni depende de que lances uno nuevo: se recalcula cada vez que abres la pantalla, con los datos de tu ficha (reseñas, fotos, categorías, publicaciones). Un cambio que acabas de hacer en Google puede tardar un poco en verse, lo que tardemos en volver a leer tu ficha.',
    },
  ],

  cierre: {
    titulo: 'Deja de adivinar cómo te ve ',
    acento: 'Google',
    sub: 'Conecta tu ficha en dos minutos, escanea tu zona y sabes en la misma sesión dónde estás y qué hacer para subir.',
  },
};

const automatizaciones = {
  hero: {
    titulo: 'Responder a tiempo te ',
    acento: 'posiciona más arriba',
    tituloB: ' en Google Maps',
    sub: 'Google valora la actividad de tu perfil, y un perfil que responde sus reseñas posiciona más arriba. Aquí se responden solas, con tu tono y en el momento justo.',
    cta: 'Probar 7 días gratis',
    ctaVer: 'Ver ejemplos de respuesta',
  },

  problema: {
    pendientes: '4 sin responder',
    titulo: 'Cada reseña que no respondes a tiempo también te cuesta posición en Google Maps',
    p1: 'Entre atender el negocio y todo lo demás, responder cada reseña una por una siempre queda para luego. No sobran minutos en el día para eso.',
    p2: 'Pero Google usa cuántas reseñas respondes como una señal para tu posición en las búsquedas locales. Así que cada reseña sin contestar no solo queda pendiente, también resta donde más te juegas: donde te busca alguien que todavía no te conoce.',
  },

  reglas: {
    kicker: 'Respuestas con IA',
    quien: {
      titulo: 'Tú decides qué reseñas se responden solas',
      // Ya no hay modo estándar: se responde siempre con IA. Ojo, no prometer
      // «nunca una plantilla»: el texto fijo sigue saliendo como paracaídas.
      cuerpo: 'Eliges qué puntuaciones se responden automáticamente, de 5 estrellas a 1, sin restricciones. Cada respuesta la escribe la IA en el momento, adaptada a esa reseña en concreto, y siempre con el tono con el que ya hablas en tu ficha.',
      // Refleja `DEFAULT_REPLY_RATINGS = [4, 5]`: de 4★ arriba, automática.
      estados: { auto: 'Respuesta automática', aviso: 'Solo aviso' },
    },
    cuando: {
      titulo: 'Ni al segundo, ni al día siguiente',
      cuerpo: 'Fijas un tiempo de espera antes de publicar cada respuesta, o dejas que varíe dentro de un rango, por ejemplo entre 15 minutos y una hora. Una respuesta en cinco segundos parece un robot; una en un rato prudente parece alguien que de verdad leyó lo que escribiste. Y sigues dentro de la ventana que más te interesa: contestar pronto es una señal que Google tiene en cuenta.',
      min: 'Al instante',
      max: '24 h',
      publicaEntre: 'Publica entre',
      y: 'y',
      desde: '15 min',
      hasta: '1 h',
    },
  },

  ejemplos: {
    titulo: 'No todas las respuestas valen lo mismo',
    cuerpo: 'Hay una diferencia real entre lo que escribirías tú a toda prisa entre cliente y cliente, y lo que recibe cada reseña cuando la IA se encarga.',
    resena: {
      autor: 'Laura M.',
      inicial: 'L',
      estrellas: 5,
      texto: 'El sushi estaba buenísimo y el servicio fue rápido.',
    },
    aMano: {
      etiqueta: 'A mano, rápido',
      texto: '«¡Gracias por tu reseña! Nos alegra que lo hayas pasado bien. ¡Vuelve pronto!»',
    },
    conIa: {
      etiqueta: 'Con IA',
      texto: '«Gracias por tus palabras, nos alegra mucho que el sushi te haya encantado y que el servicio fuera rápido, es algo que cuidamos mucho. Te esperamos pronto para que pruebes alguna novedad de la carta.»',
    },
    remate: 'La respuesta rápida cumple el trámite. La de la IA demuestra que hay alguien detrás, justo lo que un cliente nuevo nota al comparar tu ficha con la de al lado.',
  },

  alertaPalabra: {
    kicker: 'Alerta por palabra clave',
    titulo: 'Hay palabras que necesitas ver en el momento, no cuando revises las reseñas del mes',
    p1: 'Eliges las palabras que te importan, y en cuanto una reseña nueva las menciona, te llega un email al instante.',
    p2: 'Sirve para lo urgente, como enterarte al momento si alguien menciona algo grave. Y también para lo inesperado: si varios clientes repiten «vegano» o «sin gluten» sin que lo ofrezcas, eso es demanda que se te está escapando.',
    urgente: { etiqueta: 'Vigila lo urgente', chips: ['cucaracha', 'intoxicación', 'Sushi Kanda'] },
    oportunidad: { etiqueta: 'Detecta la oportunidad', chips: ['vegano', 'sin gluten', 'terraza'] },
  },

  alertaNota: {
    kicker: 'Alerta por valoración baja',
    titulo: 'Entérate de la reseña mala en minutos, no cuando ya la ha leído todo el mundo',
    cuerpo: 'Eliges qué puntuaciones vigilar, de 1 a 3 estrellas, y en cuanto llega una reseña con esa nota te avisa por email al momento. No hace falta revisar el panel por si acaso: te enteras justo cuando pasa, con tiempo real para reaccionar antes de que quede días visible sin respuesta.',
    email: {
      bandeja: 'Bandeja de entrada',
      ahora: 'ahora',
      de: 'MyTapstar',
      remite: 'alertas@mytapstar.com',
      asunto: 'Nueva reseña de 2★ en tu negocio',
      extracto: '«La comida bien, pero tardaron bastante en atendernos y nadie…»',
      sello: 'Recibido 4 min después',
    },
  },

  porQue: {
    kicker: 'Por qué importa',
    cifra: '9-15 %',
    cifraPie: 'de conversión de clientes nuevos se pierde con una mala reseña sin responder',
    titulo: 'Una mala reseña sin responder cuesta más de lo que parece',
    p1: 'Una reseña negativa sin respuesta no se queda quieta: sigue ahí, visible, mientras cada persona que la lee confía un poco menos en ti. Ese efecto tiene un coste medible, entre un 9 % y un 15 % de conversión de clientes nuevos, y una respuesta en menos de una hora puede revertir casi todo el daño.',
    p2: 'No es solo cortesía. Es la diferencia entre una queja aislada y la razón por la que alguien elige a tu competencia sin decírtelo.',
  },

  faqTitulo: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Las respuestas de la IA se publican solas o las reviso antes?',
      a: 'Se publican automáticamente, según el retardo que hayas configurado. No hay un paso intermedio de revisión manual para las puntuaciones que decides automatizar.',
    },
    {
      q: '¿Puedo elegir el tono de las respuestas automáticas?',
      // Los tonos viven en ajustes (`SettingsSerializer.tones`), cada uno con
      // su `applied_locations` y su `rating_bucket`; la regla elige cuál usar
      // con `tone_name`. O sea: ni uno solo para todo, ni uno por regla.
      a: 'Sí. Los tonos se definen una vez y se reutilizan: cada uno dice a qué locales se aplica —a todos o a los que elijas— y si es para las reseñas buenas o para las críticas, así que la voz con la que agradeces un 5 estrellas puede no ser la misma con la que respondes un 1.',
    },
    {
      q: 'Tengo varios locales, ¿configuro las reglas una vez o por cada uno?',
      a: 'Cada local puede tener su propia configuración, y además hay una regla general que cubre todos a la vez si prefieres no ir uno por uno.',
    },
    {
      q: '¿Cuántas palabras clave puedo vigilar a la vez?',
      // El tope real es `MAX_KEYWORDS_PER_RULE = 20`. El diseño decía «las que
      // necesites, no hay límite», y al pasarse el backend responde 400.
      a: 'Hasta 20 por regla. Como puedes tener una regla general y otra por local, en la práctica das de sobra para cubrir lo que de verdad quieres vigilar.',
    },
    {
      q: '¿Qué pasa si elijo responder automáticamente y también recibir alerta por la misma puntuación?',
      a: 'Se activan las dos a la vez: la reseña se responde automáticamente y, además, te llega el email de alerta. Así no te pierdes el aviso aunque la respuesta ya se haya publicado sola.',
    },
  ],

  cierre: {
    titulo: 'Deja de perseguir reseñas, ',
    acento: 'que las persigan por ti',
    sub: 'Configura las reglas una vez y cada reseña se responde, se vigila o te avisa sola, sin que tengas que acordarte de entrar cada día.',
  },
};

const publicacionesIa = {
  hero: {
    badge: '¿Cuándo fue la última vez que publicaste algo en tu ficha de Google?',
    titulo: 'Publicar con regularidad te ',
    acento: 'posiciona más arriba',
    tituloB: ' en Google Maps',
    sub: 'Escribe la idea, la IA redacta la publicación, y se programa sola aunque tengas varios locales.',
    cta: 'Probar 7 días gratis',
    ctaVer: 'Ver un ejemplo real',
    preview: {
      etiqueta: 'Vista previa',
      tipo: 'Oferta',
      texto: '¡Estamos emocionados de anunciar una increíble promoción: 2x1 en hamburguesas! Ven a vernos y disfruta de la mejor carne de la ciudad 🍔🔥',
      boton: 'Reservar',
    },
  },

  problema: {
    titulo: 'Tu ficha de Google probablemente lleva meses sin decir nada',
    p1: 'La mayoría de negocios nunca ha publicado nada en su ficha de Google. Y los que lo probaron alguna vez, normalmente lo dejaron a la primera: escribir el texto, buscar una foto, subirla, elegir el botón; para una sola publicación no compensa el rato que lleva.',
    p2: 'El problema es que Google premia con más visibilidad a los perfiles que publican con regularidad. Así que ese silencio no es solo una ficha que se ve vacía, es otra señal que le falta a tu posicionamiento, igual que pasaba con las reseñas sin responder.',
  },

  crear: {
    kicker: 'Crea publicaciones con IA',
    titulo: 'Escribe la idea, la IA hace el resto',
    cuerpo: 'Cuéntale en un par de líneas qué quieres publicar, adjunta una foto si tienes, y la IA escribe el texto completo, listo para revisar y publicar.',
    tuLado: 'Lo que escribes tú',
    suLado: 'Lo que te devuelve',
    prompt: {
      etiqueta: 'Tu idea',
      texto: '2x1 en la hamburguesa estrella, solo este jueves',
      tipoEtiqueta: 'Tipo',
      tipo: 'Oferta',
      botonEtiqueta: 'Botón',
      boton: 'Reservar',
      generar: 'Generar con IA',
    },
    salida: {
      texto: '🍔 ¡2x1 en nuestra hamburguesa estrella! Doble carne de Angus a la parrilla, queso fundido, salsa de la casa y pan brioche recién horneado, con patatas premium cortadas a mano. 🔥 Promoción válida solo este jueves, todo el día. Te esperamos en Calle Mayor 14 📍 Ven con quien más te apetezca y que cada uno pida la suya. 😋',
      boton: 'Reservar',
    },
  },

  tipos: {
    titulo: 'Cada publicación con su tipo y su botón',
    cuerpo: 'Eliges si es una actualización, una oferta o un evento, y qué botón lleva debajo: más información, reservar, pedir, comprar, registrarse, llamar, o ninguno.',
    // Los tres `VALID_TOPIC_TYPES` reales: STANDARD, OFFER, EVENT.
    lista: [
      { clave: 'standard', n: 'Actualización', d: 'Novedades del día a día', color: '#1a73e8' },
      { clave: 'offer', n: 'Oferta', d: 'Promociones con fecha', color: '#feae2c' },
      { clave: 'event', n: 'Evento', d: 'Algo que pasa un día concreto', color: '#7c3aed' },
    ],
    botonesEtiqueta: 'Botones disponibles',
    // Los seis `VALID_CTA_ACTIONS` + la opción de no poner ninguno.
    botones: ['Más información', 'Reservar', 'Pedir', 'Comprar', 'Registrarse', 'Llamar', 'Sin botón'],
  },

  programar: {
    titulo: 'Programa hoy, publica cuando tú digas',
    // SIN la repetición semanal/mensual del diseño: no existe.
    cuerpo: 'Eliges la fecha y la hora, o la dejas para ahora mismo, y se publica sola. Cada publicación queda guardada con su estado —programada, publicada o fallida— y un enlace directo a sus métricas en Google.',
    diasSemana: ['L', 'M', 'X', 'J', 'V', 'S', 'D'],
    recientes: [
      { clave: 'p1', t: '2x1 en hamburguesas', color: '#feae2c', estado: 'Publicada', tono: 'bien' },
      { clave: 'p2', t: 'Nueva carta de temporada', color: '#1a73e8', estado: 'Programada', tono: 'medio' },
    ],
  },

  multi: {
    kicker: 'Multiubicación',
    titulo: 'Una publicación, personalizada para cada sede',
    cuerpo: 'Si tienes varios locales, escribes la publicación una vez usando variables como el nombre del negocio, la ciudad, la dirección o el teléfono, y se publica adaptada automáticamente en cada sede. Nada de repetir el mismo trabajo local por local.',
    variablesEtiqueta: 'Variables disponibles',
    // Las cinco `POST_VARIABLE_KEYS` reales, en su orden canónico.
    variables: [
      { k: '{{name}}', d: 'Nombre del negocio' },
      { k: '{{city}}', d: 'Ciudad' },
      { k: '{{address}}', d: 'Dirección' },
      { k: '{{phone}}', d: 'Teléfono' },
      { k: '{{website}}', d: 'Página web' },
    ],
  },

  porQue: {
    kicker: 'Por qué importa',
    titulo: 'Google no trata igual a quien publica y a quien no',
    p1: 'No hace falta publicar todos los días. Pero los perfiles que lo hacen con cierta regularidad aparecen más arriba en Maps que los que llevan meses en silencio, con el resto de factores iguales.',
    p2: 'Es una de las señales de posicionamiento más fáciles de ganar, y de las que menos negocios aprovechan.',
  },

  faqTitulo: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Necesito subir una foto siempre?',
      a: 'No, es opcional. Si no adjuntas ninguna, la publicación sale solo con texto.',
    },
    {
      q: '¿Puedo editar lo que escribe la IA antes de publicar?',
      a: 'Sí, el texto generado es completamente editable. No se publica nada sin que lo veas antes.',
    },
    {
      q: '¿Qué tipos de publicación hay?',
      a: 'Actualización, oferta y evento, cada uno con su propio color en el calendario.',
    },
    {
      // Sustituye a la de la repetición automática, que prometía una función
      // que no existe. Esta cubre lo que sí se puede hacer.
      q: '¿Con cuánta antelación puedo programar una publicación?',
      a: 'Hasta 180 días. Es el mismo plazo durante el que guardamos la foto que adjuntas, así que más allá de ahí la publicación saldría sin imagen.',
    },
    {
      q: 'Tengo varios locales, ¿tengo que escribir una publicación distinta para cada uno?',
      a: 'No si usas variables: escribes una vez y cada local recibe su versión adaptada automáticamente.',
    },
  ],

  cierre: {
    titulo: 'Deja de improvisar tu ',
    acento: 'ficha de Google',
    sub: 'Escribe la idea, la IA la convierte en publicación, y tú decides cuándo sale.',
  },
};

const widgetResenas = {
  hero: {
    badge: 'Tus reseñas de Google, ahora en tu propia web',
    titulo: 'Muestra la confianza que ',
    acento: 'ya te has ganado',
    sub: 'Copia un código, pégalo en tu web, y tus mejores reseñas de Google empiezan a trabajar para convencer a quien todavía no te conoce.',
    cta: 'Probar 7 días gratis',
    ctaVer: 'Ver los dos formatos',
  },

  formatos: {
    kicker: 'Dos formatos',
    resumen: {
      etiqueta: 'Resumen',
      titulo: 'El sello de confianza en cinco segundos',
      cuerpo: 'Tu nota media, el número de reseñas y el logo de Google, todo en una tarjeta compacta. Es lo primero que confirma que eres de fiar, antes de que nadie tenga que leer nada.',
      // Tal cual lo pinta el widget: el nombre del local sin tocar, ni
      // mayúsculas ni tracking. El total lleva su palabra, que sale de
      // `WIDGET_LABELS` según el idioma elegido.
      negocio: 'Tu negocio',
      nota: '4,7',
      total: '1.284 reseñas',
    },
    carrusel: {
      etiqueta: 'Carrusel',
      titulo: 'Las voces reales de tus clientes, en movimiento',
      cuerpo: 'Tus reseñas más recientes, con foto, nombre y fecha, desplazándose solas en tu web. Se actualiza cada vez que llega una nueva, sin que tengas que tocar nada.',
      // Fechas en dd/mm/aaaa, que es como las formatea el widget
      // (`review.created_at|date:"d/m/Y"`), no «hace 2 días». Y sin colores de
      // avatar: sin foto de perfil el widget pinta la inicial sobre el gris
      // del borde, siempre el mismo.
      resenas: [
        {
          clave: 'laura', autor: 'Laura M.', inicial: 'L', fecha: '18/09/2026', estrellas: 5,
          texto: 'Todo genial, el trato inmejorable y la comida buenísima. Repetiremos seguro.',
        },
        {
          clave: 'diego', autor: 'Diego R.', inicial: 'D', fecha: '15/09/2026', estrellas: 4,
          texto: 'Muy buena relación calidad precio, volveremos seguro.',
        },
        {
          clave: 'marc', autor: 'Marc T.', inicial: 'M', fecha: '11/09/2026', estrellas: 5,
          texto: 'Sitio muy agradable y personal atentísimo. Muy recomendable.',
        },
      ],
    },
  },

  personalizar: {
    titulo: 'Que combine con tu web, no que la interrumpa',
    cuerpo: 'Eliges el tema (claro, oscuro o el del visitante), el idioma, y un color de acento que por defecto ya sale con el tuyo de marca. Ajustas hasta el redondeo de las esquinas. Y en el carrusel decides si se ven las fotos de perfil, las fechas, o si solo quieres mostrar las reseñas de 5 estrellas.',
    temaEtiqueta: 'Tema',
    temas: ['Claro', 'Oscuro'],
    colorEtiqueta: 'Color de acento',
    colorNota: 'Tu color de marca',
    redondeoEtiqueta: 'Redondeo de esquinas',
    redondeoValor: '14 px',
    soloCarrusel: 'Solo en el carrusel',
    opciones: [
      { clave: 'avatares', n: 'Fotos de perfil', on: true },
      { clave: 'fechas', n: 'Fechas de la reseña', on: true },
      { clave: 'cinco', n: 'Solo reseñas de 5 estrellas', on: false },
    ],
  },

  dominios: {
    titulo: 'Nadie puede copiarlo a su propia web',
    // El diseño decía «no se muestra en ninguna parte». Sin dominios el
    // `frame-ancestors` se queda en `'self'`, así que dentro de nuestro panel
    // sigue viéndose — que es lo que hace funcionar la vista previa.
    cuerpo: 'El widget solo funciona en los dominios que tú autorices. Mientras no añadas ninguno, no se muestra en ninguna web ajena: solo en la vista previa de tu panel, para que puedas dejarlo a tu gusto antes de publicarlo.',
    etiqueta: 'Dominios permitidos',
    ejemplo: 'tunegocio.com',
    activo: 'Activo',
    anadir: 'Añadir otro dominio…',
    anadirCta: 'Añadir',
  },

  porQue: {
    kicker: 'Por qué importa',
    cifra: '88 %',
    cifraPie: 'confía en una reseña casi tanto como en un amigo',
    titulo: 'El 88 % confía en una reseña casi tanto como en la recomendación de un amigo',
    p1: 'Esa confianza vale mucho más si la reseña está donde el cliente ya está mirando, en tu propia web, no escondida en una ficha de Google a la que tiene que ir a buscarla por su cuenta.',
  },

  faqTitulo: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Necesito saber programar para instalarlo?',
      a: 'No, copias dos líneas de código y las pegas en tu web, como cualquier otro widget.',
    },
    {
      q: '¿Ralentiza mi web?',
      a: 'No, el código se carga de forma asíncrona y no bloquea el resto de la página.',
    },
    {
      q: '¿Funciona en cualquier plataforma?',
      a: 'Sí, en cualquier web que te permita pegar código HTML: WordPress, Shopify o una hecha a medida.',
    },
    {
      q: '¿Se actualiza solo cuando llega una reseña nueva?',
      a: 'Sí, no hace falta regenerar el código ni tocar nada.',
    },
    {
      q: '¿Puedo usar el resumen y el carrusel a la vez?',
      a: 'Sí, en la misma página o en páginas distintas de tu web.',
    },
  ],

  cierre: {
    titulo: 'Pon tus reseñas donde tus clientes ',
    acento: 'ya están mirando',
    sub: 'Usa el resumen, el carrusel, o los dos a la vez en la misma página: el resumen como primer vistazo, el carrusel justo debajo para quien quiera leer más. Copia el código y ya está en tu web.',
  },
};

/**
 * Lo que las cuatro páginas dicen IGUAL en el cierre.
 *
 * El botón, el microcopy y la línea de enterprise son literalmente el mismo
 * texto en las cuatro (y en las de Soluciones). Va una vez: cuatro copias es
 * lo que hace que dentro de un año una diga «7 días» y otra «14».
 *
 * El titular de cada una sí es propio, y vive en su `cierre`.
 */
const comun = {
  cierre: {
    cta: 'Probar 7 días gratis',
    microcopy: '7 días gratis · sin compromiso · cancelas cuando quieras',
    enterpriseA: '¿Más de 30 locales? ',
    enterpriseCta: 'Contacta con ventas',
  },

  /** Lo que lee un lector de pantalla donde se pintan ★★★★☆. `{n}` es la nota. */
  estrellasEtiqueta: '{n} de 5 estrellas',
};

/*
 * ── LAS TRADUCCIONES ────────────────────────────────────────────────────────
 *
 * Reglas generales en `landingV3.home.js`. Lo propio de este fichero:
 *
 *  - **Los topes del Análisis SEO son números, y son los de `health_score.py`.**
 *    `p` y `t` de `categorias` suman 100 y no se tocan; solo se traduce el
 *    nombre de la categoría.
 *  - **Las tres cifras del escáner son las de la captura de al lado.** `7,9`,
 *    `89,8 %` y `20,4 %` se localizan la coma y el espacio, pero no el número:
 *    quien mire el texto y la imagen a la vez ve enseguida si uno de los dos
 *    está inventado.
 *  - **`{{name}}`, `{{city}}`, `{{address}}`, `{{phone}}` y `{{website}}` son
 *    literales.** Son las cinco `POST_VARIABLE_KEYS`; el usuario las escribe
 *    tal cual en el panel y traducirlas las rompe.
 *  - **Las fechas del carrusel van en `d/m/Y` en los cinco idiomas**, porque es
 *    como las formatea el widget de verdad, no como se escriben en cada país.
 *  - Y lo de siempre: las cinco afirmaciones que la cabecera de este fichero
 *    dice que NO se copian del diseño siguen sin copiarse. La recurrencia de
 *    las publicaciones, el percentil, el «sin límite» de palabras clave, el
 *    «nunca una plantilla» y el «no se muestra en ninguna parte» son promesas
 *    que el producto no cumple, y en una traducción vuelven a colarse solas.
 */
const visibilidadSeoEn = {
  hero: {
    badge: 'Do you know what position you come up in when your customers search?',
    titulo: 'Own your area on ',
    acento: 'Google Maps',
    sub: 'Attract more customers by coming up higher, not just on your own doorstep.',
    cta: 'Try 7 days free',
    ctaVer: 'See the scanner in action',
    consulta: '“sushi restaurant near me”',
    etiquetaZona: 'Your area',
  },

  problema: {
    pinCerca: 'On your street',
    pinLejos: '800 m away',
    titulo: 'Being on Google Maps is not the same as showing up on Google Maps',
    p1: 'If you search for your own business, or search while standing in it, you will almost always come up well. But a real customer does not search for you by name: they search something like “sushi restaurant near me” from another point in your area, not knowing you exist yet.',
    p2: 'That is where your position changes. You can be first on your own street and not even make the second page a few hundred metres away. It is a blind spot almost nobody spots, because the normal way of checking — searching for yourself — is exactly the one that never reveals it.',
  },

  escaneo: {
    kicker: 'Position scan',
    titulo: 'This is how Google sees you from every point in your area',
    cuerpo: 'You choose what your customers search for (“sushi restaurant” or “sushi near me”, for example) and the size of the area around your business. In seconds you have a map with your exact position at every point: green if you come up among the first, red if you struggle to appear, grey if you do not appear at all.',
    captura: {
      ...CAPTURA_MAPA,
      alt: 'The SEO map in the dashboard: on the left the scan form with the chosen location and the results, on the right the grid of positions over the city map.',
    },
    kpis: [
      {
        clave: 'media',
        l: 'Average position',
        v: '7.9',
        d: 'Your average ranking, counting only the points where you appear',
      },
      {
        clave: 'cobertura',
        l: 'Coverage',
        v: '89.8%',
        d: 'At how many of the scanned points you show up, even if low down',
      },
      {
        clave: 'dominancia',
        l: 'Local dominance',
        v: '20.4%',
        d: 'At what percentage of points you are in the top 3',
      },
    ],
  },

  competidores: {
    kicker: 'Competitors',
    titulo: 'Compare yourself with whoever really competes for your customers',
    cuerpo: 'You pick the rival by name: the same business your customers get when they search for what you sell. You choose them from the list of businesses in your area, with their rating and their reviews in view, and you compare their position with yours, point by point. That shows you something an average never would: you may be doing well on your own street while a few hundred metres away they come up before you.',
  },

  analisis: {
    kicker: 'SEO analysis',
    titulo: 'You know exactly what is holding you back, not just that you are doing badly',
    p1: 'Your listing gets a score from 0 to 100 that sums up how ready it is to rank well, with levels running from “Just starting” to “Local leader”.',
    p2: 'But the number itself is not what changes things. What matters is that behind that score we know exactly what is weighing on it: whether you are short of recent reviews, whether your listing has gone months without posting, whether the categories do not match what people search for, or whether you simply need up-to-date photos.',
    p3: 'It is not an opinion or a black box: it comes from your listing’s real data. So when you change something, the score really moves — not because we say so.',
    p4: 'And rather than leaving you with a list of twenty things to look at, we tell you which one will move the needle most. That is where you start, not wherever you feel like.',

    tarjeta: {
      puntuacion: '62',
      sobre: '/ 100',
      nivelEtiqueta: 'Current level',
      nivel: 'On the way up',
      faltan: 'You are 9 points away from “Well positioned”.',
      metricas: [
        { clave: 'sinResponder', l: 'Unanswered reviews', v: '4', tono: 'mal' },
        { clave: 'tasa', l: 'Reply rate (30 days)', v: '62% · target 80%', tono: 'medio' },
        { clave: 'evolucion', l: 'Change this month', v: '+6 pts', tono: 'bien' },
      ],
      mision: {
        etiqueta: 'Next mission',
        texto: 'Answer reviews within 24 hours',
        puntos: '+8 pts when completed',
      },
      categorias: [
        { clave: 'nap', n: 'NAP details', p: 20, t: 20 },
        { clave: 'cat', n: 'Categories', p: 16, t: 20 },
        { clave: 'vis', n: 'Visual presence', p: 12, t: 18 },
        { clave: 'rep', n: 'Reputation', p: 8, t: 20 },
        { clave: 'srv', n: 'Keywords and services', p: 6, t: 12 },
        { clave: 'act', n: 'Activity', p: 0, t: 10 },
      ],
    },
  },

  porQue: {
    kicker: 'Why it matters',
    cifra: '40-50%',
    cifraPie: 'of search clicks go to the map of the top three',
    titulo: 'The first result does not just take the first click, it takes half of them',
    p1: 'When someone searches for a business like yours, they hardly ever get as far as opening a website. They see the map with the top three results (the “local pack”) and decide right there.',
    p2: 'That map takes between 40 and 50% of all the clicks on the search. If you come up fourth or lower, you are left with a tiny fraction of that traffic, even if your business is better than the three above you.',
    p3: 'It is not a small margin you can ignore. It is the difference between being found and not being seen at all.',
  },

  faqTitulo: 'Frequently asked questions',
  faq: [
    {
      q: 'Do I need technical knowledge to use it?',
      a: 'No. You connect your Google Business listing in two minutes and have your first scan in the same session.',
    },
    {
      q: 'How many points does it scan, and what does that depend on?',
      a: 'You choose the size of the grid around your business, from a small area to wide coverage. The more area you cover, the more points you scan: a 5×5 is 25 points and a 7×7 is 49. Each point costs one credit, and the balance you have left is always in view on the scanner screen itself.',
    },
    {
      q: 'Can I compare against more than one competitor at a time?',
      a: 'You see the full list of businesses in your category in the area, with their average position and their local dominance. You compare against one at a time, but you can switch competitor whenever you like without spending a new scan.',
    },
    {
      q: 'I have several locations — does it work the same?',
      a: 'Yes. Each location has its own grid and its own score, and you can see a ranking across all your locations to know which one needs the most attention.',
    },
    {
      q: 'Does the SEO analysis score update on its own, or do I have to scan again?',
      a: 'It updates on its own. It is not tied to the SEO map scans, so it does not spend credits or depend on you launching a new one: it is recalculated every time you open the screen, using your listing’s data (reviews, photos, categories, posts). A change you have just made in Google may take a little while to show, as long as it takes us to read your listing again.',
    },
  ],

  cierre: {
    titulo: 'Stop guessing how ',
    acento: 'Google',
    sub: 'Connect your listing in two minutes, scan your area and know in the same session where you stand and what to do to climb.',
  },
};

const automatizacionesEn = {
  hero: {
    titulo: 'Answering in time ',
    acento: 'ranks you higher',
    tituloB: ' on Google Maps',
    sub: 'Google values how active your profile is, and a profile that answers its reviews ranks higher. Here they answer themselves, in your tone and at the right moment.',
    cta: 'Try 7 days free',
    ctaVer: 'See example replies',
  },

  problema: {
    pendientes: '4 unanswered',
    titulo: 'Every review you do not answer in time also costs you position on Google Maps',
    p1: 'Between running the business and everything else, answering each review one by one always gets put off. There are no spare minutes in the day for it.',
    p2: 'But Google uses how many reviews you answer as a signal for your position in local searches. So every unanswered review is not just a pending task, it also costs you where it matters most: where someone who does not know you yet is looking.',
  },

  reglas: {
    kicker: 'AI replies',
    quien: {
      titulo: 'You decide which reviews get answered on their own',
      cuerpo: 'You choose which ratings get answered automatically, from 5 stars down to 1, with no restrictions. Each reply is written by AI on the spot, tailored to that particular review, and always in the tone you already use on your listing.',
      estados: { auto: 'Automatic reply', aviso: 'Alert only' },
    },
    cuando: {
      titulo: 'Not within a second, not the next day',
      cuerpo: 'You set a waiting time before each reply is published, or let it vary within a range — between 15 minutes and an hour, for instance. A reply in five seconds looks like a robot; one after a sensible while looks like someone who actually read what you wrote. And you stay inside the window that suits you best: answering promptly is a signal Google takes into account.',
      min: 'Straight away',
      max: '24 h',
      publicaEntre: 'Publishes between',
      y: 'and',
      desde: '15 min',
      hasta: '1 h',
    },
  },

  ejemplos: {
    titulo: 'Not every reply is worth the same',
    cuerpo: 'There is a real difference between what you would dash off between customers and what every review gets when the AI takes care of it.',
    resena: {
      autor: 'Laura M.',
      inicial: 'L',
      estrellas: 5,
      texto: 'The sushi was delicious and the service was quick.',
    },
    aMano: {
      etiqueta: 'By hand, in a hurry',
      texto: '“Thanks for your review! We are glad you had a good time. Come back soon!”',
    },
    conIa: {
      etiqueta: 'With AI',
      texto: '“Thank you for your kind words — we are delighted that you loved the sushi and that the service was quick, it is something we take great care over. We hope to see you soon so you can try something new from the menu.”',
    },
    remate: 'The quick reply ticks the box. The AI one shows there is someone behind it, which is exactly what a new customer notices when they compare your listing with the one next door.',
  },

  alertaPalabra: {
    kicker: 'Keyword alert',
    titulo: 'Some words you need to see the moment they appear, not when you go through the month’s reviews',
    p1: 'You choose the words that matter to you, and as soon as a new review mentions one, you get an email straight away.',
    p2: 'It is for the urgent things, like finding out the moment someone mentions something serious. And for the unexpected too: if several customers keep saying “vegan” or “gluten-free” when you do not offer it, that is demand slipping past you.',
    urgente: { etiqueta: 'Watch the urgent', chips: ['cockroach', 'food poisoning', 'Sushi Kanda'] },
    oportunidad: { etiqueta: 'Spot the opportunity', chips: ['vegan', 'gluten-free', 'terrace'] },
  },

  alertaNota: {
    kicker: 'Low-rating alert',
    titulo: 'Hear about the bad review in minutes, not once everyone has already read it',
    cuerpo: 'You choose which ratings to watch, from 1 to 3 stars, and as soon as a review comes in with that rating it emails you right away. No need to check the dashboard just in case: you find out exactly when it happens, with real time to react before it sits there unanswered for days.',
    email: {
      bandeja: 'Inbox',
      ahora: 'now',
      de: 'MyTapstar',
      remite: 'alertas@mytapstar.com',
      asunto: 'New 2★ review at your business',
      extracto: '“The food was fine, but they took quite a while to serve us and nobody…”',
      sello: 'Received 4 min later',
    },
  },

  porQue: {
    kicker: 'Why it matters',
    cifra: '9-15%',
    cifraPie: 'of new-customer conversion is lost to an unanswered bad review',
    titulo: 'An unanswered bad review costs more than it looks',
    p1: 'A negative review with no reply does not sit still: it stays there, visible, while everyone who reads it trusts you a little less. That effect has a measurable cost, between 9% and 15% of new-customer conversion, and a reply within the hour can undo almost all the damage.',
    p2: 'It is not just courtesy. It is the difference between an isolated complaint and the reason someone chooses your competitor without telling you.',
  },

  faqTitulo: 'Frequently asked questions',
  faq: [
    {
      q: 'Are the AI replies published on their own, or do I review them first?',
      a: 'They are published automatically, after whatever delay you have set. There is no intermediate manual review step for the ratings you choose to automate.',
    },
    {
      q: 'Can I choose the tone of the automatic replies?',
      a: 'Yes. Tones are defined once and reused: each one says which locations it applies to — all of them or the ones you choose — and whether it is for good reviews or critical ones, so the voice you thank a 5-star review with need not be the one you answer a 1-star with.',
    },
    {
      q: 'I have several locations — do I set the rules up once or for each one?',
      a: 'Each location can have its own setup, and there is also a general rule covering all of them at once if you would rather not go one by one.',
    },
    {
      q: 'How many keywords can I watch at a time?',
      a: 'Up to 20 per rule. Since you can have one general rule and another per location, in practice that is more than enough to cover what you really want to watch.',
    },
    {
      q: 'What happens if I choose to answer automatically and also get an alert for the same rating?',
      a: 'Both happen: the review is answered automatically and you also get the alert email. So you do not miss the notice even though the reply has already gone out on its own.',
    },
  ],

  cierre: {
    titulo: 'Stop chasing reviews, ',
    acento: 'let them be chased for you',
    sub: 'Set the rules up once and every review gets answered, watched or flagged on its own, without you having to remember to log in each day.',
  },
};

const publicacionesIaEn = {
  hero: {
    badge: 'When did you last post anything on your Google listing?',
    titulo: 'Posting regularly ',
    acento: 'ranks you higher',
    tituloB: ' on Google Maps',
    sub: 'Write the idea, the AI drafts the post, and it schedules itself even if you have several locations.',
    cta: 'Try 7 days free',
    ctaVer: 'See a real example',
    preview: {
      etiqueta: 'Preview',
      tipo: 'Offer',
      texto: 'We are thrilled to announce an amazing deal: 2-for-1 on burgers! Come and see us and enjoy the best beef in town 🍔🔥',
      boton: 'Book',
    },
  },

  problema: {
    titulo: 'Your Google listing has probably said nothing for months',
    p1: 'Most businesses have never posted anything on their Google listing. And those that tried once usually gave up straight away: write the text, find a photo, upload it, choose the button — for a single post it is not worth the time it takes.',
    p2: 'The problem is that Google rewards profiles that post regularly with more visibility. So that silence is not just a listing that looks empty, it is another signal your ranking is missing, exactly as with unanswered reviews.',
  },

  crear: {
    kicker: 'Create posts with AI',
    titulo: 'Write the idea, the AI does the rest',
    cuerpo: 'Tell it in a couple of lines what you want to post, attach a photo if you have one, and the AI writes the full text, ready to review and publish.',
    tuLado: 'What you write',
    suLado: 'What you get back',
    prompt: {
      etiqueta: 'Your idea',
      texto: '2-for-1 on the signature burger, this Thursday only',
      tipoEtiqueta: 'Type',
      tipo: 'Offer',
      botonEtiqueta: 'Button',
      boton: 'Book',
      generar: 'Generate with AI',
    },
    salida: {
      texto: '🍔 2-for-1 on our signature burger! Double grilled Angus beef, melted cheese, house sauce and freshly baked brioche, with premium hand-cut chips. 🔥 Offer valid this Thursday only, all day. We are waiting for you at 14 High Street 📍 Bring whoever you fancy and have one each. 😋',
      boton: 'Book',
    },
  },

  tipos: {
    titulo: 'Every post with its type and its button',
    cuerpo: 'You choose whether it is an update, an offer or an event, and which button goes underneath: learn more, book, order, buy, sign up, call, or none.',
    lista: [
      { clave: 'standard', n: 'Update', d: 'Day-to-day news', color: '#1a73e8' },
      { clave: 'offer', n: 'Offer', d: 'Promotions with a date', color: '#feae2c' },
      { clave: 'event', n: 'Event', d: 'Something happening on a set day', color: '#7c3aed' },
    ],
    botonesEtiqueta: 'Buttons available',
    botones: ['Learn more', 'Book', 'Order', 'Buy', 'Sign up', 'Call', 'No button'],
  },

  programar: {
    titulo: 'Schedule today, publish whenever you say',
    cuerpo: 'You choose the date and time, or leave it for right now, and it publishes on its own. Every post is saved with its status — scheduled, published or failed — and a direct link to its metrics in Google.',
    diasSemana: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    recientes: [
      { clave: 'p1', t: '2-for-1 on burgers', color: '#feae2c', estado: 'Published', tono: 'bien' },
      { clave: 'p2', t: 'New seasonal menu', color: '#1a73e8', estado: 'Scheduled', tono: 'medio' },
    ],
  },

  multi: {
    kicker: 'Multi-location',
    titulo: 'One post, personalised for every site',
    cuerpo: 'If you have several locations, you write the post once using variables such as the business name, the city, the address or the phone number, and it publishes adapted at each site automatically. No repeating the same work location by location.',
    variablesEtiqueta: 'Variables available',
    variables: [
      { k: '{{name}}', d: 'Business name' },
      { k: '{{city}}', d: 'City' },
      { k: '{{address}}', d: 'Address' },
      { k: '{{phone}}', d: 'Phone' },
      { k: '{{website}}', d: 'Website' },
    ],
  },

  porQue: {
    kicker: 'Why it matters',
    titulo: 'Google does not treat those who post and those who do not the same',
    p1: 'You do not have to post every day. But profiles that do it with some regularity come up higher on Maps than those that have been silent for months, all else being equal.',
    p2: 'It is one of the easiest ranking signals to earn, and one of the least used.',
  },

  faqTitulo: 'Frequently asked questions',
  faq: [
    {
      q: 'Do I always need to upload a photo?',
      a: 'No, it is optional. If you do not attach one, the post goes out as text only.',
    },
    {
      q: 'Can I edit what the AI writes before publishing?',
      a: 'Yes, the generated text is fully editable. Nothing is published without you seeing it first.',
    },
    {
      q: 'What kinds of post are there?',
      a: 'Update, offer and event, each with its own colour in the calendar.',
    },
    {
      q: 'How far ahead can I schedule a post?',
      a: 'Up to 180 days. That is the same period we keep the photo you attach for, so beyond that the post would go out without an image.',
    },
    {
      q: 'I have several locations — do I have to write a different post for each one?',
      a: 'Not if you use variables: you write once and each location gets its own adapted version automatically.',
    },
  ],

  cierre: {
    titulo: 'Stop improvising your ',
    acento: 'Google listing',
    sub: 'Write the idea, the AI turns it into a post, and you decide when it goes out.',
  },
};

const widgetResenasEn = {
  hero: {
    badge: 'Your Google reviews, now on your own website',
    titulo: 'Show the trust you ',
    acento: 'have already earned',
    sub: 'Copy a snippet, paste it on your site, and your best Google reviews start working to convince whoever does not know you yet.',
    cta: 'Try 7 days free',
    ctaVer: 'See both formats',
  },

  formatos: {
    kicker: 'Two formats',
    resumen: {
      etiqueta: 'Summary',
      titulo: 'The trust badge in five seconds',
      cuerpo: 'Your average rating, the number of reviews and the Google logo, all in one compact card. It is the first thing that confirms you can be trusted, before anyone has to read a word.',
      negocio: 'Your business',
      nota: '4.7',
      total: '1,284 reviews',
    },
    carrusel: {
      etiqueta: 'Carousel',
      titulo: 'Your customers’ real voices, in motion',
      cuerpo: 'Your most recent reviews, with photo, name and date, scrolling by on your site. It updates every time a new one arrives, without you touching anything.',
      resenas: [
        {
          clave: 'laura', autor: 'Laura M.', inicial: 'L', fecha: '18/09/2026', estrellas: 5,
          texto: 'All great, the service could not be better and the food was delicious. We will definitely be back.',
        },
        {
          clave: 'diego', autor: 'Diego R.', inicial: 'D', fecha: '15/09/2026', estrellas: 4,
          texto: 'Very good value for money, we will be back for sure.',
        },
        {
          clave: 'marc', autor: 'Marc T.', inicial: 'M', fecha: '11/09/2026', estrellas: 5,
          texto: 'Lovely place and extremely attentive staff. Highly recommended.',
        },
      ],
    },
  },

  personalizar: {
    titulo: 'Something that fits your site rather than interrupting it',
    cuerpo: 'You choose the theme (light, dark or the visitor’s), the language, and an accent colour that already comes set to your brand’s. You even adjust how rounded the corners are. And in the carousel you decide whether profile photos show, whether dates show, or whether you only want to display 5-star reviews.',
    temaEtiqueta: 'Theme',
    temas: ['Light', 'Dark'],
    colorEtiqueta: 'Accent colour',
    colorNota: 'Your brand colour',
    redondeoEtiqueta: 'Corner rounding',
    redondeoValor: '14 px',
    soloCarrusel: 'Carousel only',
    opciones: [
      { clave: 'avatares', n: 'Profile photos', on: true },
      { clave: 'fechas', n: 'Review dates', on: true },
      { clave: 'cinco', n: 'Only 5-star reviews', on: false },
    ],
  },

  dominios: {
    titulo: 'Nobody can copy it onto their own website',
    cuerpo: 'The widget only works on the domains you authorise. Until you add one, it does not show on anyone else’s website: only in the preview in your dashboard, so you can get it exactly how you want before publishing it.',
    etiqueta: 'Allowed domains',
    ejemplo: 'yourbusiness.com',
    activo: 'Active',
    anadir: 'Add another domain…',
    anadirCta: 'Add',
  },

  porQue: {
    kicker: 'Why it matters',
    cifra: '88%',
    cifraPie: 'trust a review almost as much as a friend',
    titulo: '88% trust a review almost as much as a friend’s recommendation',
    p1: 'That trust is worth far more if the review is where the customer is already looking — on your own website, not tucked away in a Google listing they have to go and find for themselves.',
  },

  faqTitulo: 'Frequently asked questions',
  faq: [
    {
      q: 'Do I need to know how to code to install it?',
      a: 'No, you copy two lines of code and paste them on your site, like any other widget.',
    },
    {
      q: 'Does it slow my website down?',
      a: 'No, the code loads asynchronously and does not block the rest of the page.',
    },
    {
      q: 'Does it work on any platform?',
      a: 'Yes, on any website that lets you paste HTML: WordPress, Shopify or a custom build.',
    },
    {
      q: 'Does it update on its own when a new review comes in?',
      a: 'Yes, there is no need to regenerate the code or touch anything.',
    },
    {
      q: 'Can I use the summary and the carousel at the same time?',
      a: 'Yes, on the same page or on different pages of your site.',
    },
  ],

  cierre: {
    titulo: 'Put your reviews where your customers ',
    acento: 'are already looking',
    sub: 'Use the summary, the carousel, or both at once on the same page: the summary as a first glance, the carousel just below for anyone who wants to read more. Copy the code and it is on your site.',
  },
};

const comunEn = {
  cierre: {
    cta: 'Try 7 days free',
    microcopy: '7 days free · no commitment · cancel whenever you like',
    enterpriseA: 'More than 30 locations? ',
    enterpriseCta: 'Contact sales',
  },

  estrellasEtiqueta: '{n} out of 5 stars',
};

const visibilidadSeoPt = {
  hero: {
    badge: 'Sabe em que posição aparece quando os seus clientes o procuram?',
    titulo: 'Domine a sua zona no ',
    acento: 'Google Maps',
    sub: 'Atraia mais clientes aparecendo em melhor posição, e não só à porta do seu negócio.',
    cta: 'Testar 7 dias grátis',
    ctaVer: 'Ver o scanner em ação',
    consulta: '«restaurante de sushi perto de mim»',
    etiquetaZona: 'A sua zona',
  },

  problema: {
    pinCerca: 'Na sua rua',
    pinLejos: 'A 800 m',
    titulo: 'Estar no Google Maps não é o mesmo que aparecer no Google Maps',
    p1: 'Se procurar o seu próprio negócio, ou procurar estando no seu local, quase sempre vai aparecer bem. Mas um cliente a sério não o procura pelo nome: procura algo como «restaurante de sushi perto de mim» a partir de outro ponto da sua zona, sem saber ainda que existe.',
    p2: 'É aí que a sua posição muda. Pode ser o primeiro na sua rua e não aparecer nem na segunda página a algumas centenas de metros. É um ponto cego que quase ninguém deteta, porque a forma normal de o verificar — procurar-se a si próprio — é precisamente a que nunca o revela.',
  },

  escaneo: {
    kicker: 'Análise de posição',
    titulo: 'É assim que o Google o vê a partir de cada ponto da sua zona',
    cuerpo: 'Escolhe o que os seus clientes procuram (por exemplo, «restaurante de sushi» ou «sushi perto de mim») e o tamanho da zona à volta do seu negócio. Em segundos tem um mapa com a sua posição exata em cada ponto: verde se aparece entre os primeiros, vermelho se lhe custa aparecer, cinzento se não aparece de todo.',
    captura: {
      ...CAPTURA_MAPA,
      alt: 'O Mapa de SEO do painel: à esquerda o formulário de análise com o local escolhido e os resultados, à direita a grelha de posições sobre o mapa da cidade.',
    },
    kpis: [
      {
        clave: 'media',
        l: 'Posição média',
        v: '7,9',
        d: 'A sua posição média, contando só os pontos onde aparece',
      },
      {
        clave: 'cobertura',
        l: 'Cobertura',
        v: '89,8 %',
        d: 'Em quantos dos pontos analisados aparece, mesmo que numa posição baixa',
      },
      {
        clave: 'dominancia',
        l: 'Domínio local',
        v: '20,4 %',
        d: 'Em que percentagem de pontos está entre os 3 primeiros',
      },
    ],
  },

  competidores: {
    kicker: 'Concorrentes',
    titulo: 'Compare-se com quem de facto compete pelos seus clientes',
    cuerpo: 'Escolhe o rival pelo nome: o mesmo negócio que aparece aos seus clientes quando procuram o que você vende. Escolhe-o da lista de negócios da sua zona, com a classificação e as avaliações à vista, e compara a posição dele com a sua, ponto a ponto. Assim vê algo que uma média nunca lhe diria: pode estar bem na sua rua e, a algumas centenas de metros, ele aparecer antes de si.',
  },

  analisis: {
    kicker: 'Análise de SEO',
    titulo: 'Sabe exatamente o que o está a travar, e não apenas que vai mal',
    p1: 'A sua ficha recebe uma pontuação de 0 a 100 que resume o quão preparada está para se posicionar bem, com níveis que vão de «A começar» até «Líder local».',
    p2: 'Mas o número em si não é o que muda as coisas. O que importa é que, por trás dessa pontuação, sabemos exatamente o que está a pesar: se lhe faltam avaliações recentes, se a sua ficha está há meses sem publicar nada, se as categorias não encaixam com o que as pessoas procuram, ou se simplesmente lhe faltam fotografias atualizadas.',
    p3: 'Não é uma opinião nem uma caixa negra: sai dos dados reais da sua ficha. Por isso, quando muda alguma coisa, a pontuação mexe-se a sério, e não conforme o que nós digamos.',
    p4: 'E em vez de o deixar com uma lista de vinte coisas para rever, dizemos-lhe qual é a que mais o vai mexer primeiro. Começa por aí, não por onde lhe parecer.',

    tarjeta: {
      puntuacion: '62',
      sobre: '/ 100',
      nivelEtiqueta: 'Nível atual',
      nivel: 'Caminho do pódio',
      faltan: 'Faltam-lhe 9 pontos para chegar a «Bem posicionado».',
      metricas: [
        { clave: 'sinResponder', l: 'Avaliações sem resposta', v: '4', tono: 'mal' },
        { clave: 'tasa', l: 'Taxa de resposta (30 dias)', v: '62 % · objetivo 80 %', tono: 'medio' },
        { clave: 'evolucion', l: 'Evolução este mês', v: '+6 pts', tono: 'bien' },
      ],
      mision: {
        etiqueta: 'Próxima missão',
        texto: 'Responda às avaliações em menos de 24 horas',
        puntos: '+8 pts ao concluir',
      },
      categorias: [
        { clave: 'nap', n: 'Dados NAP', p: 20, t: 20 },
        { clave: 'cat', n: 'Categorias', p: 16, t: 20 },
        { clave: 'vis', n: 'Presença visual', p: 12, t: 18 },
        { clave: 'rep', n: 'Reputação', p: 8, t: 20 },
        { clave: 'srv', n: 'Palavras-chave e serviços', p: 6, t: 12 },
        { clave: 'act', n: 'Atividade', p: 0, t: 10 },
      ],
    },
  },

  porQue: {
    kicker: 'Porque importa',
    cifra: '40-50 %',
    cifraPie: 'dos cliques da pesquisa vão para o mapa dos três primeiros',
    titulo: 'O primeiro resultado não leva só o primeiro clique, leva metade deles',
    p1: 'Quando alguém procura um negócio como o seu, quase nunca chega a abrir um site. Vê o mapa com os três primeiros resultados (o «pacote local») e decide ali mesmo.',
    p2: 'Esse mapa leva entre 40 e 50 % de todos os cliques da pesquisa. Se aparecer na quarta posição ou mais abaixo, fica-lhe uma fração mínima desse tráfego, mesmo que o seu negócio seja melhor do que os três de cima.',
    p3: 'Não é uma margem pequena que se possa ignorar. É a diferença entre o encontrarem ou nem o verem.',
  },

  faqTitulo: 'Perguntas frequentes',
  faq: [
    {
      q: 'Preciso de conhecimentos técnicos para o usar?',
      a: 'Não. Liga a sua ficha do Google Business em dois minutos e tem a primeira análise na mesma sessão.',
    },
    {
      q: 'Quantos pontos analisa e do que depende?',
      a: 'Escolhe o tamanho da grelha à volta do seu negócio, de uma zona pequena a uma cobertura ampla. Quanto mais zona cobrir, mais pontos analisa: uma de 5×5 são 25 pontos e uma de 7×7 são 49. Cada ponto custa um crédito, e o saldo que lhe resta está sempre à vista no próprio ecrã do scanner.',
    },
    {
      q: 'Posso comparar com mais do que um concorrente ao mesmo tempo?',
      a: 'Vê a lista completa de negócios da sua categoria na zona, com a posição média e o domínio local de cada um. Compara contra um de cada vez, mas pode mudar de concorrente quando quiser sem gastar uma análise nova.',
    },
    {
      q: 'Tenho vários locais, funciona igual?',
      a: 'Sim. Cada local tem a sua própria grelha e a sua própria pontuação, e pode ver um ranking comparativo entre todos os seus locais para saber qual precisa de mais atenção.',
    },
    {
      q: 'A pontuação da Análise de SEO atualiza-se sozinha ou é preciso analisar de novo?',
      a: 'Atualiza-se sozinha. Não está ligada às análises do Mapa de SEO, por isso não gasta créditos nem depende de lançar uma nova: recalcula-se sempre que abre o ecrã, com os dados da sua ficha (avaliações, fotografias, categorias, publicações). Uma alteração que acabou de fazer no Google pode demorar um pouco a ver-se, o tempo que demorarmos a voltar a ler a sua ficha.',
    },
  ],

  cierre: {
    titulo: 'Deixe de adivinhar como o vê o ',
    acento: 'Google',
    sub: 'Ligue a sua ficha em dois minutos, analise a sua zona e saiba na mesma sessão onde está e o que fazer para subir.',
  },
};

const automatizacionesPt = {
  hero: {
    titulo: 'Responder a tempo ',
    acento: 'posiciona-o mais acima',
    tituloB: ' no Google Maps',
    sub: 'O Google valoriza a atividade do seu perfil, e um perfil que responde às avaliações posiciona-se mais acima. Aqui respondem-se sozinhas, com o seu tom e no momento certo.',
    cta: 'Testar 7 dias grátis',
    ctaVer: 'Ver exemplos de resposta',
  },

  problema: {
    pendientes: '4 sem resposta',
    titulo: 'Cada avaliação a que não responde a tempo também lhe custa posição no Google Maps',
    p1: 'Entre tratar do negócio e tudo o resto, responder a cada avaliação uma a uma fica sempre para depois. Não sobram minutos no dia para isso.',
    p2: 'Mas o Google usa quantas avaliações responde como sinal para a sua posição nas pesquisas locais. Por isso cada avaliação sem resposta não fica só pendente, também tira onde mais se joga: onde o procura alguém que ainda não o conhece.',
  },

  reglas: {
    kicker: 'Respostas com IA',
    quien: {
      titulo: 'É você que decide que avaliações se respondem sozinhas',
      cuerpo: 'Escolhe que classificações se respondem automaticamente, de 5 estrelas a 1, sem restrições. Cada resposta é escrita pela IA na hora, adaptada àquela avaliação em concreto, e sempre com o tom com que já fala na sua ficha.',
      estados: { auto: 'Resposta automática', aviso: 'Só aviso' },
    },
    cuando: {
      titulo: 'Nem ao segundo, nem no dia seguinte',
      cuerpo: 'Define um tempo de espera antes de publicar cada resposta, ou deixa que varie dentro de um intervalo, por exemplo entre 15 minutos e uma hora. Uma resposta em cinco segundos parece um robô; uma passado um bocado prudente parece alguém que leu mesmo o que escreveu. E continua dentro da janela que mais lhe interessa: responder cedo é um sinal que o Google tem em conta.',
      min: 'De imediato',
      max: '24 h',
      publicaEntre: 'Publica entre',
      y: 'e',
      desde: '15 min',
      hasta: '1 h',
    },
  },

  ejemplos: {
    titulo: 'Nem todas as respostas valem o mesmo',
    cuerpo: 'Há uma diferença real entre o que escreveria à pressa entre cliente e cliente e o que cada avaliação recebe quando é a IA a tratar disso.',
    resena: {
      autor: 'Laura M.',
      inicial: 'L',
      estrellas: 5,
      texto: 'O sushi estava ótimo e o serviço foi rápido.',
    },
    aMano: {
      etiqueta: 'À mão, à pressa',
      texto: '«Obrigado pela sua avaliação! Ficamos contentes por ter gostado. Volte em breve!»',
    },
    conIa: {
      etiqueta: 'Com IA',
      texto: '«Obrigado pelas suas palavras, ficamos muito contentes por ter adorado o sushi e por o serviço ter sido rápido, é algo de que cuidamos muito. Esperamos por si em breve para provar alguma novidade da carta.»',
    },
    remate: 'A resposta rápida cumpre o trâmite. A da IA mostra que há alguém do outro lado, precisamente o que um cliente novo nota ao comparar a sua ficha com a do lado.',
  },

  alertaPalabra: {
    kicker: 'Alerta por palavra-chave',
    titulo: 'Há palavras que precisa de ver na hora, não quando rever as avaliações do mês',
    p1: 'Escolhe as palavras que lhe importam e, assim que uma avaliação nova as menciona, chega-lhe um email de imediato.',
    p2: 'Serve para o urgente, como ficar a saber na hora se alguém menciona algo grave. E também para o inesperado: se vários clientes repetem «vegano» ou «sem glúten» sem que o ofereça, isso é procura que lhe está a escapar.',
    urgente: { etiqueta: 'Vigie o urgente', chips: ['barata', 'intoxicação', 'Sushi Kanda'] },
    oportunidad: { etiqueta: 'Detete a oportunidade', chips: ['vegano', 'sem glúten', 'esplanada'] },
  },

  alertaNota: {
    kicker: 'Alerta por classificação baixa',
    titulo: 'Fique a saber da avaliação má em minutos, não quando toda a gente já a leu',
    cuerpo: 'Escolhe que classificações vigiar, de 1 a 3 estrelas, e assim que chega uma avaliação com essa nota avisa-o por email na hora. Não é preciso rever o painel por via das dúvidas: fica a saber mesmo quando acontece, com tempo real para reagir antes de ficar dias visível sem resposta.',
    email: {
      bandeja: 'Caixa de entrada',
      ahora: 'agora',
      de: 'MyTapstar',
      remite: 'alertas@mytapstar.com',
      asunto: 'Nova avaliação de 2★ no seu negócio',
      extracto: '«A comida bem, mas demoraram bastante a atender-nos e ninguém…»',
      sello: 'Recebido 4 min depois',
    },
  },

  porQue: {
    kicker: 'Porque importa',
    cifra: '9-15 %',
    cifraPie: 'de conversão de clientes novos perde-se com uma avaliação má sem resposta',
    titulo: 'Uma avaliação má sem resposta custa mais do que parece',
    p1: 'Uma avaliação negativa sem resposta não fica quieta: continua ali, visível, enquanto cada pessoa que a lê confia um pouco menos em si. Esse efeito tem um custo mensurável, entre 9 % e 15 % de conversão de clientes novos, e uma resposta em menos de uma hora pode reverter quase todo o dano.',
    p2: 'Não é só cortesia. É a diferença entre uma queixa isolada e a razão pela qual alguém escolhe a concorrência sem lho dizer.',
  },

  faqTitulo: 'Perguntas frequentes',
  faq: [
    {
      q: 'As respostas da IA publicam-se sozinhas ou reviu-as antes?',
      a: 'Publicam-se automaticamente, conforme o atraso que tiver configurado. Não há um passo intermédio de revisão manual para as classificações que decide automatizar.',
    },
    {
      q: 'Posso escolher o tom das respostas automáticas?',
      a: 'Sim. Os tons definem-se uma vez e reutilizam-se: cada um diz a que locais se aplica — a todos ou aos que escolher — e se é para as avaliações boas ou para as críticas, por isso a voz com que agradece um 5 estrelas pode não ser a mesma com que responde a um 1.',
    },
    {
      q: 'Tenho vários locais, configuro as regras uma vez ou para cada um?',
      a: 'Cada local pode ter a sua própria configuração, e além disso há uma regra geral que cobre todos ao mesmo tempo se preferir não ir um a um.',
    },
    {
      q: 'Quantas palavras-chave posso vigiar ao mesmo tempo?',
      a: 'Até 20 por regra. Como pode ter uma regra geral e outra por local, na prática dá de sobra para cobrir o que de facto quer vigiar.',
    },
    {
      q: 'O que acontece se escolher responder automaticamente e também receber alerta pela mesma classificação?',
      a: 'Ativam-se as duas ao mesmo tempo: a avaliação é respondida automaticamente e, além disso, chega-lhe o email de alerta. Assim não perde o aviso mesmo que a resposta já tenha sido publicada sozinha.',
    },
  ],

  cierre: {
    titulo: 'Deixe de perseguir avaliações, ',
    acento: 'que as persigam por si',
    sub: 'Configure as regras uma vez e cada avaliação responde-se, vigia-se ou avisa-o sozinha, sem que tenha de se lembrar de entrar todos os dias.',
  },
};

const publicacionesIaPt = {
  hero: {
    badge: 'Quando foi a última vez que publicou alguma coisa na sua ficha do Google?',
    titulo: 'Publicar com regularidade ',
    acento: 'posiciona-o mais acima',
    tituloB: ' no Google Maps',
    sub: 'Escreve a ideia, a IA redige a publicação, e agenda-se sozinha mesmo que tenha vários locais.',
    cta: 'Testar 7 dias grátis',
    ctaVer: 'Ver um exemplo real',
    preview: {
      etiqueta: 'Pré-visualização',
      tipo: 'Promoção',
      texto: 'Estamos entusiasmados por anunciar uma promoção incrível: 2x1 em hambúrgueres! Venha ver-nos e desfrute da melhor carne da cidade 🍔🔥',
      boton: 'Reservar',
    },
  },

  problema: {
    titulo: 'A sua ficha do Google provavelmente está há meses sem dizer nada',
    p1: 'A maioria dos negócios nunca publicou nada na sua ficha do Google. E os que experimentaram uma vez normalmente desistiram logo: escrever o texto, procurar uma fotografia, carregá-la, escolher o botão; para uma só publicação não compensa o tempo que leva.',
    p2: 'O problema é que o Google premeia com mais visibilidade os perfis que publicam com regularidade. Por isso esse silêncio não é só uma ficha que se vê vazia, é mais um sinal que falta ao seu posicionamento, tal como acontecia com as avaliações sem resposta.',
  },

  crear: {
    kicker: 'Crie publicações com IA',
    titulo: 'Escreva a ideia, a IA faz o resto',
    cuerpo: 'Conte-lhe em duas linhas o que quer publicar, junte uma fotografia se tiver, e a IA escreve o texto completo, pronto para rever e publicar.',
    tuLado: 'O que escreve',
    suLado: 'O que lhe devolve',
    prompt: {
      etiqueta: 'A sua ideia',
      texto: '2x1 no hambúrguer estrela, só esta quinta-feira',
      tipoEtiqueta: 'Tipo',
      tipo: 'Promoção',
      botonEtiqueta: 'Botão',
      boton: 'Reservar',
      generar: 'Gerar com IA',
    },
    salida: {
      texto: '🍔 2x1 no nosso hambúrguer estrela! Carne de Angus dupla grelhada, queijo derretido, molho da casa e pão brioche acabado de fazer, com batatas premium cortadas à mão. 🔥 Promoção válida só esta quinta-feira, todo o dia. Esperamos por si na Rua Principal 14 📍 Venha com quem lhe apetecer e que cada um peça o seu. 😋',
      boton: 'Reservar',
    },
  },

  tipos: {
    titulo: 'Cada publicação com o seu tipo e o seu botão',
    cuerpo: 'Escolhe se é uma atualização, uma promoção ou um evento, e que botão leva por baixo: mais informações, reservar, encomendar, comprar, registar-se, ligar, ou nenhum.',
    lista: [
      { clave: 'standard', n: 'Atualização', d: 'Novidades do dia a dia', color: '#1a73e8' },
      { clave: 'offer', n: 'Promoção', d: 'Campanhas com data', color: '#feae2c' },
      { clave: 'event', n: 'Evento', d: 'Algo que acontece num dia concreto', color: '#7c3aed' },
    ],
    botonesEtiqueta: 'Botões disponíveis',
    botones: ['Mais informações', 'Reservar', 'Encomendar', 'Comprar', 'Registar-se', 'Ligar', 'Sem botão'],
  },

  programar: {
    titulo: 'Agende hoje, publique quando disser',
    cuerpo: 'Escolhe a data e a hora, ou deixa para já, e publica-se sozinha. Cada publicação fica guardada com o seu estado — agendada, publicada ou falhada — e uma ligação direta às suas métricas no Google.',
    diasSemana: ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'],
    recientes: [
      { clave: 'p1', t: '2x1 em hambúrgueres', color: '#feae2c', estado: 'Publicada', tono: 'bien' },
      { clave: 'p2', t: 'Nova carta de época', color: '#1a73e8', estado: 'Agendada', tono: 'medio' },
    ],
  },

  multi: {
    kicker: 'Multilocalização',
    titulo: 'Uma publicação, personalizada para cada sede',
    cuerpo: 'Se tem vários locais, escreve a publicação uma vez usando variáveis como o nome do negócio, a cidade, a morada ou o telefone, e publica-se adaptada automaticamente em cada sede. Nada de repetir o mesmo trabalho local a local.',
    variablesEtiqueta: 'Variáveis disponíveis',
    variables: [
      { k: '{{name}}', d: 'Nome do negócio' },
      { k: '{{city}}', d: 'Cidade' },
      { k: '{{address}}', d: 'Morada' },
      { k: '{{phone}}', d: 'Telefone' },
      { k: '{{website}}', d: 'Site' },
    ],
  },

  porQue: {
    kicker: 'Porque importa',
    titulo: 'O Google não trata da mesma forma quem publica e quem não publica',
    p1: 'Não é preciso publicar todos os dias. Mas os perfis que o fazem com alguma regularidade aparecem mais acima no Maps do que os que estão há meses em silêncio, com os restantes fatores iguais.',
    p2: 'É um dos sinais de posicionamento mais fáceis de ganhar, e dos que menos negócios aproveitam.',
  },

  faqTitulo: 'Perguntas frequentes',
  faq: [
    {
      q: 'Tenho de carregar sempre uma fotografia?',
      a: 'Não, é opcional. Se não juntar nenhuma, a publicação sai só com texto.',
    },
    {
      q: 'Posso editar o que a IA escreve antes de publicar?',
      a: 'Sim, o texto gerado é completamente editável. Nada é publicado sem que o veja antes.',
    },
    {
      q: 'Que tipos de publicação existem?',
      a: 'Atualização, promoção e evento, cada um com a sua própria cor no calendário.',
    },
    {
      q: 'Com quanta antecedência posso agendar uma publicação?',
      a: 'Até 180 dias. É o mesmo prazo durante o qual guardamos a fotografia que junta, por isso para além disso a publicação sairia sem imagem.',
    },
    {
      q: 'Tenho vários locais, tenho de escrever uma publicação diferente para cada um?',
      a: 'Não, se usar variáveis: escreve uma vez e cada local recebe a sua versão adaptada automaticamente.',
    },
  ],

  cierre: {
    titulo: 'Deixe de improvisar a sua ',
    acento: 'ficha do Google',
    sub: 'Escreve a ideia, a IA transforma-a em publicação, e você decide quando sai.',
  },
};

const widgetResenasPt = {
  hero: {
    badge: 'As suas avaliações do Google, agora no seu próprio site',
    titulo: 'Mostre a confiança que ',
    acento: 'já conquistou',
    sub: 'Copie um código, cole-o no seu site, e as suas melhores avaliações do Google começam a trabalhar para convencer quem ainda não o conhece.',
    cta: 'Testar 7 dias grátis',
    ctaVer: 'Ver os dois formatos',
  },

  formatos: {
    kicker: 'Dois formatos',
    resumen: {
      etiqueta: 'Resumo',
      titulo: 'O selo de confiança em cinco segundos',
      cuerpo: 'A sua média, o número de avaliações e o logótipo do Google, tudo num cartão compacto. É a primeira coisa que confirma que é de confiança, antes de alguém ter de ler o que quer que seja.',
      negocio: 'O seu negócio',
      nota: '4,7',
      total: '1.284 avaliações',
    },
    carrusel: {
      etiqueta: 'Carrossel',
      titulo: 'As vozes reais dos seus clientes, em movimento',
      cuerpo: 'As suas avaliações mais recentes, com fotografia, nome e data, a deslizar sozinhas no seu site. Atualiza-se sempre que chega uma nova, sem que tenha de tocar em nada.',
      resenas: [
        {
          clave: 'laura', autor: 'Laura M.', inicial: 'L', fecha: '18/09/2026', estrellas: 5,
          texto: 'Tudo ótimo, o atendimento imbatível e a comida muito boa. Vamos repetir de certeza.',
        },
        {
          clave: 'diego', autor: 'Diego R.', inicial: 'D', fecha: '15/09/2026', estrellas: 4,
          texto: 'Muito boa relação qualidade-preço, voltaremos de certeza.',
        },
        {
          clave: 'marc', autor: 'Marc T.', inicial: 'M', fecha: '11/09/2026', estrellas: 5,
          texto: 'Sítio muito agradável e pessoal atentíssimo. Muito recomendável.',
        },
      ],
    },
  },

  personalizar: {
    titulo: 'Que combine com o seu site, não que o interrompa',
    cuerpo: 'Escolhe o tema (claro, escuro ou o do visitante), o idioma, e uma cor de destaque que por predefinição já sai com a da sua marca. Ajusta até o arredondamento dos cantos. E no carrossel decide se se veem as fotografias de perfil, as datas, ou se só quer mostrar as avaliações de 5 estrelas.',
    temaEtiqueta: 'Tema',
    temas: ['Claro', 'Escuro'],
    colorEtiqueta: 'Cor de destaque',
    colorNota: 'A cor da sua marca',
    redondeoEtiqueta: 'Arredondamento dos cantos',
    redondeoValor: '14 px',
    soloCarrusel: 'Só no carrossel',
    opciones: [
      { clave: 'avatares', n: 'Fotografias de perfil', on: true },
      { clave: 'fechas', n: 'Datas da avaliação', on: true },
      { clave: 'cinco', n: 'Só avaliações de 5 estrelas', on: false },
    ],
  },

  dominios: {
    titulo: 'Ninguém o pode copiar para o seu próprio site',
    cuerpo: 'O widget só funciona nos domínios que autorizar. Enquanto não adicionar nenhum, não se mostra em nenhum site alheio: só na pré-visualização do seu painel, para que o possa deixar a seu gosto antes de o publicar.',
    etiqueta: 'Domínios permitidos',
    ejemplo: 'oseunegocio.pt',
    activo: 'Ativo',
    anadir: 'Adicionar outro domínio…',
    anadirCta: 'Adicionar',
  },

  porQue: {
    kicker: 'Porque importa',
    cifra: '88 %',
    cifraPie: 'confia numa avaliação quase tanto como num amigo',
    titulo: '88 % confiam numa avaliação quase tanto como na recomendação de um amigo',
    p1: 'Essa confiança vale muito mais se a avaliação estiver onde o cliente já está a olhar, no seu próprio site, e não escondida numa ficha do Google que tem de ir procurar por sua conta.',
  },

  faqTitulo: 'Perguntas frequentes',
  faq: [
    {
      q: 'Preciso de saber programar para o instalar?',
      a: 'Não, copia duas linhas de código e cola-as no seu site, como qualquer outro widget.',
    },
    {
      q: 'Torna o meu site mais lento?',
      a: 'Não, o código carrega de forma assíncrona e não bloqueia o resto da página.',
    },
    {
      q: 'Funciona em qualquer plataforma?',
      a: 'Sim, em qualquer site que lhe permita colar código HTML: WordPress, Shopify ou um feito à medida.',
    },
    {
      q: 'Atualiza-se sozinho quando chega uma avaliação nova?',
      a: 'Sim, não é preciso gerar o código de novo nem tocar em nada.',
    },
    {
      q: 'Posso usar o resumo e o carrossel ao mesmo tempo?',
      a: 'Sim, na mesma página ou em páginas diferentes do seu site.',
    },
  ],

  cierre: {
    titulo: 'Ponha as suas avaliações onde os seus clientes ',
    acento: 'já estão a olhar',
    sub: 'Use o resumo, o carrossel, ou os dois ao mesmo tempo na mesma página: o resumo como primeiro olhar, o carrossel logo abaixo para quem quiser ler mais. Copie o código e já está no seu site.',
  },
};

const comunPt = {
  cierre: {
    cta: 'Testar 7 dias grátis',
    microcopy: '7 dias grátis · sem compromisso · cancela quando quiser',
    enterpriseA: 'Mais de 30 locais? ',
    enterpriseCta: 'Fale com vendas',
  },

  estrellasEtiqueta: '{n} de 5 estrelas',
};

const visibilidadSeoDe = {
  hero: {
    badge: 'Weißt du, auf welcher Position du erscheinst, wenn deine Kunden suchen?',
    titulo: 'Beherrsche deine Gegend auf ',
    acento: 'Google Maps',
    sub: 'Gewinne mehr Kunden, indem du weiter oben erscheinst — nicht nur direkt vor deiner Tür.',
    cta: '7 Tage kostenlos testen',
    ctaVer: 'Den Scanner in Aktion sehen',
    consulta: '„Sushi-Restaurant in meiner Nähe“',
    etiquetaZona: 'Deine Gegend',
  },

  problema: {
    pinCerca: 'In deiner Straße',
    pinLejos: '800 m entfernt',
    titulo: 'Bei Google Maps zu sein ist nicht dasselbe wie bei Google Maps aufzutauchen',
    p1: 'Wenn du nach deinem eigenen Unternehmen suchst — oder suchst, während du darin stehst —, erscheinst du fast immer gut. Aber ein echter Kunde sucht dich nicht beim Namen: Er sucht so etwas wie „Sushi-Restaurant in meiner Nähe“ von einem anderen Punkt deiner Gegend aus, ohne zu wissen, dass es dich gibt.',
    p2: 'Genau da ändert sich deine Position. Du kannst in deiner Straße Erster sein und ein paar hundert Meter weiter nicht einmal auf der zweiten Seite auftauchen. Ein blinder Fleck, den kaum jemand bemerkt — weil die übliche Art, das zu prüfen (nach sich selbst suchen), genau die ist, die ihn nie aufdeckt.',
  },

  escaneo: {
    kicker: 'Positions-Scan',
    titulo: 'So sieht Google dich von jedem Punkt deiner Gegend aus',
    cuerpo: 'Du wählst, wonach deine Kunden suchen (zum Beispiel „Sushi-Restaurant“ oder „Sushi in meiner Nähe“) und wie groß die Gegend um dein Unternehmen ist. In Sekunden hast du eine Karte mit deiner genauen Position an jedem Punkt: grün, wenn du unter den Ersten erscheinst, rot, wenn du dich schwertust, grau, wenn du gar nicht auftauchst.',
    captura: {
      ...CAPTURA_MAPA,
      alt: 'Die SEO-Karte im Dashboard: links das Scan-Formular mit dem gewählten Standort und den Ergebnissen, rechts das Raster der Positionen über der Stadtkarte.',
    },
    kpis: [
      {
        clave: 'media',
        l: 'Durchschnittliche Position',
        v: '7,9',
        d: 'Deine mittlere Position, gezählt nur an den Punkten, an denen du erscheinst',
      },
      {
        clave: 'cobertura',
        l: 'Abdeckung',
        v: '89,8 %',
        d: 'An wie vielen der gescannten Punkte du auftauchst, und sei es weit unten',
      },
      {
        clave: 'dominancia',
        l: 'Lokale Dominanz',
        v: '20,4 %',
        d: 'An wie viel Prozent der Punkte du unter den ersten 3 bist',
      },
    ],
  },

  competidores: {
    kicker: 'Wettbewerber',
    titulo: 'Vergleich dich mit dem, der wirklich um deine Kunden kämpft',
    cuerpo: 'Du wählst den Wettbewerber beim Namen: genau das Unternehmen, das deinen Kunden angezeigt wird, wenn sie nach dem suchen, was du verkaufst. Du wählst ihn aus der Liste der Unternehmen in deiner Gegend, mit seiner Bewertung und seinen Rezensionen im Blick, und vergleichst seine Position mit deiner, Punkt für Punkt. So siehst du etwas, das ein Durchschnitt dir nie verraten würde: In deiner Straße läufst du vielleicht gut, und ein paar hundert Meter weiter erscheint er vor dir.',
  },

  analisis: {
    kicker: 'SEO-Analyse',
    titulo: 'Du weißt genau, was dich ausbremst — nicht nur, dass es schlecht läuft',
    p1: 'Dein Profil bekommt eine Punktzahl von 0 bis 100, die zusammenfasst, wie gut es aufgestellt ist, um gut zu ranken — mit Leveln von „Am Anfang“ bis „Lokaler Marktführer“.',
    p2: 'Aber die Zahl allein ändert nichts. Entscheidend ist, dass wir hinter dieser Punktzahl genau wissen, was ins Gewicht fällt: ob dir aktuelle Bewertungen fehlen, ob dein Profil seit Monaten nichts veröffentlicht hat, ob die Kategorien nicht zu dem passen, wonach die Leute suchen, oder ob dir schlicht aktuelle Fotos fehlen.',
    p3: 'Das ist keine Meinung und keine Blackbox: Es kommt aus den echten Daten deines Profils. Wenn du also etwas änderst, bewegt sich die Punktzahl wirklich — nicht, weil wir es sagen.',
    p4: 'Und statt dich mit einer Liste von zwanzig Punkten sitzen zu lassen, sagen wir dir, welcher dich zuerst am weitesten bringt. Da fängst du an, nicht irgendwo.',

    tarjeta: {
      puntuacion: '62',
      sobre: '/ 100',
      nivelEtiqueta: 'Aktuelles Level',
      nivel: 'Auf dem Weg nach oben',
      faltan: 'Dir fehlen 9 Punkte bis „Gut positioniert“.',
      metricas: [
        { clave: 'sinResponder', l: 'Unbeantwortete Bewertungen', v: '4', tono: 'mal' },
        { clave: 'tasa', l: 'Antwortquote (30 Tage)', v: '62 % · Ziel 80 %', tono: 'medio' },
        { clave: 'evolucion', l: 'Entwicklung diesen Monat', v: '+6 Pkt.', tono: 'bien' },
      ],
      mision: {
        etiqueta: 'Nächste Mission',
        texto: 'Beantworte Bewertungen in unter 24 Stunden',
        puntos: '+8 Pkt. nach Abschluss',
      },
      categorias: [
        { clave: 'nap', n: 'NAP-Daten', p: 20, t: 20 },
        { clave: 'cat', n: 'Kategorien', p: 16, t: 20 },
        { clave: 'vis', n: 'Visuelle Präsenz', p: 12, t: 18 },
        { clave: 'rep', n: 'Reputation', p: 8, t: 20 },
        { clave: 'srv', n: 'Keywords und Leistungen', p: 6, t: 12 },
        { clave: 'act', n: 'Aktivität', p: 0, t: 10 },
      ],
    },
  },

  porQue: {
    kicker: 'Warum das zählt',
    cifra: '40-50 %',
    cifraPie: 'der Klicks einer Suche gehen an die Karte mit den ersten drei',
    titulo: 'Das erste Ergebnis holt nicht nur den ersten Klick, es holt die Hälfte',
    p1: 'Wenn jemand ein Unternehmen wie deins sucht, öffnet er fast nie eine Website. Er sieht die Karte mit den ersten drei Treffern (das „Local Pack“) und entscheidet direkt dort.',
    p2: 'Diese Karte holt zwischen 40 und 50 % aller Klicks der Suche. Erscheinst du auf Platz 4 oder tiefer, bleibt dir ein winziger Bruchteil dieses Traffics — auch wenn dein Unternehmen besser ist als die drei darüber.',
    p3: 'Das ist keine kleine Marge, die man ignorieren kann. Das ist der Unterschied zwischen gefunden werden und gar nicht gesehen werden.',
  },

  faqTitulo: 'Häufige Fragen',
  faq: [
    {
      q: 'Brauche ich technische Kenntnisse, um es zu nutzen?',
      a: 'Nein. Du verbindest dein Google-Business-Profil in zwei Minuten und hast deinen ersten Scan in derselben Sitzung.',
    },
    {
      q: 'Wie viele Punkte werden gescannt, und wovon hängt das ab?',
      a: 'Du wählst die Größe des Rasters um dein Unternehmen, von einer kleinen Gegend bis zu einer weiten Abdeckung. Je mehr Fläche du abdeckst, desto mehr Punkte scannst du: ein 5×5 sind 25 Punkte, ein 7×7 sind 49. Jeder Punkt kostet ein Guthaben, und dein Restguthaben ist im Scanner-Bildschirm immer sichtbar.',
    },
    {
      q: 'Kann ich mit mehr als einem Wettbewerber gleichzeitig vergleichen?',
      a: 'Du siehst die vollständige Liste der Unternehmen deiner Kategorie in der Gegend, mit ihrer durchschnittlichen Position und ihrer lokalen Dominanz. Du vergleichst mit einem nach dem anderen, kannst den Wettbewerber aber jederzeit wechseln, ohne einen neuen Scan zu verbrauchen.',
    },
    {
      q: 'Ich habe mehrere Standorte — funktioniert es genauso?',
      a: 'Ja. Jeder Standort hat sein eigenes Raster und seine eigene Punktzahl, und du siehst ein Ranking über alle deine Standorte, um zu wissen, welcher am meisten Aufmerksamkeit braucht.',
    },
    {
      q: 'Aktualisiert sich die Punktzahl der SEO-Analyse von allein, oder muss ich neu scannen?',
      a: 'Sie aktualisiert sich von allein. Sie hängt nicht an den Scans der SEO-Karte, kostet also kein Guthaben und setzt keinen neuen Scan voraus: Sie wird jedes Mal neu berechnet, wenn du den Bildschirm öffnest, mit den Daten deines Profils (Bewertungen, Fotos, Kategorien, Beiträge). Eine Änderung, die du gerade bei Google gemacht hast, kann etwas brauchen, bis sie zu sehen ist — so lange, wie wir zum erneuten Auslesen deines Profils brauchen.',
    },
  ],

  cierre: {
    titulo: 'Hör auf zu raten, wie ',
    acento: 'Google',
    sub: 'Verbinde dein Profil in zwei Minuten, scanne deine Gegend und weiß in derselben Sitzung, wo du stehst und was zu tun ist.',
  },
};

const automatizacionesDe = {
  hero: {
    titulo: 'Rechtzeitig zu antworten ',
    acento: 'bringt dich weiter nach oben',
    tituloB: ' bei Google Maps',
    sub: 'Google bewertet, wie aktiv dein Profil ist, und ein Profil, das seine Bewertungen beantwortet, rankt höher. Hier beantworten sie sich von allein, in deinem Ton und im richtigen Moment.',
    cta: '7 Tage kostenlos testen',
    ctaVer: 'Beispielantworten ansehen',
  },

  problema: {
    pendientes: '4 unbeantwortet',
    titulo: 'Jede Bewertung, die du nicht rechtzeitig beantwortest, kostet dich auch Position bei Google Maps',
    p1: 'Zwischen Betrieb und allem anderen bleibt es immer liegen, jede Bewertung einzeln zu beantworten. Dafür sind im Tag keine Minuten übrig.',
    p2: 'Aber Google nutzt es als Signal für deine Position in lokalen Suchen, wie viele Bewertungen du beantwortest. Jede unbeantwortete Bewertung bleibt also nicht nur offen, sie zieht auch dort ab, wo am meisten auf dem Spiel steht: dort, wo jemand sucht, der dich noch nicht kennt.',
  },

  reglas: {
    kicker: 'Antworten mit KI',
    quien: {
      titulo: 'Du entscheidest, welche Bewertungen von allein beantwortet werden',
      cuerpo: 'Du wählst, welche Bewertungen automatisch beantwortet werden, von 5 Sternen bis 1, ohne Einschränkung. Jede Antwort schreibt die KI im Moment, auf genau diese Bewertung zugeschnitten, und immer in dem Ton, in dem du in deinem Profil ohnehin sprichst.',
      estados: { auto: 'Automatische Antwort', aviso: 'Nur Hinweis' },
    },
    cuando: {
      titulo: 'Nicht in einer Sekunde, nicht erst am nächsten Tag',
      cuerpo: 'Du legst eine Wartezeit fest, bevor jede Antwort veröffentlicht wird, oder lässt sie innerhalb einer Spanne schwanken, zum Beispiel zwischen 15 Minuten und einer Stunde. Eine Antwort nach fünf Sekunden wirkt wie ein Roboter; eine nach einer angemessenen Weile wirkt wie jemand, der wirklich gelesen hat, was du geschrieben hast. Und du bleibst in dem Fenster, das dir am meisten bringt: zügig zu antworten ist ein Signal, das Google berücksichtigt.',
      min: 'Sofort',
      max: '24 Std.',
      publicaEntre: 'Veröffentlicht zwischen',
      y: 'und',
      desde: '15 Min.',
      hasta: '1 Std.',
    },
  },

  ejemplos: {
    titulo: 'Nicht jede Antwort ist gleich viel wert',
    cuerpo: 'Es gibt einen echten Unterschied zwischen dem, was du zwischen zwei Kunden schnell hinschreiben würdest, und dem, was jede Bewertung bekommt, wenn die KI übernimmt.',
    resena: {
      autor: 'Laura M.',
      inicial: 'L',
      estrellas: 5,
      texto: 'Das Sushi war köstlich und der Service war schnell.',
    },
    aMano: {
      etiqueta: 'Von Hand, schnell',
      texto: '„Danke für deine Bewertung! Wir freuen uns, dass es dir gefallen hat. Komm bald wieder!“',
    },
    conIa: {
      etiqueta: 'Mit KI',
      texto: '„Vielen Dank für deine Worte — es freut uns sehr, dass dir das Sushi so gut geschmeckt hat und der Service schnell war, darauf achten wir besonders. Wir freuen uns, dich bald wiederzusehen, damit du etwas Neues von der Karte probieren kannst.“',
    },
    remate: 'Die schnelle Antwort erledigt die Pflicht. Die der KI zeigt, dass da jemand dahintersteht — genau das, was ein neuer Kunde bemerkt, wenn er dein Profil mit dem daneben vergleicht.',
  },

  alertaPalabra: {
    kicker: 'Hinweis bei Stichwort',
    titulo: 'Manche Wörter musst du sofort sehen, nicht erst beim Durchgehen der Bewertungen des Monats',
    p1: 'Du wählst die Wörter, die dir wichtig sind, und sobald eine neue Bewertung sie erwähnt, bekommst du sofort eine E-Mail.',
    p2: 'Das hilft beim Dringenden, etwa sofort zu erfahren, wenn jemand etwas Ernstes erwähnt. Und beim Unerwarteten: Wenn mehrere Kunden „vegan“ oder „glutenfrei“ wiederholen, ohne dass du das anbietest, entgeht dir Nachfrage.',
    urgente: { etiqueta: 'Behalte das Dringende im Blick', chips: ['Kakerlake', 'Lebensmittelvergiftung', 'Sushi Kanda'] },
    oportunidad: { etiqueta: 'Erkenne die Gelegenheit', chips: ['vegan', 'glutenfrei', 'Terrasse'] },
  },

  alertaNota: {
    kicker: 'Hinweis bei niedriger Bewertung',
    titulo: 'Erfahre von der schlechten Bewertung in Minuten, nicht wenn sie schon alle gelesen haben',
    cuerpo: 'Du wählst, welche Bewertungen du im Blick behalten willst, von 1 bis 3 Sternen, und sobald eine mit dieser Note eingeht, meldet es sich sofort per E-Mail. Du musst nicht sicherheitshalber ins Dashboard schauen: Du erfährst es genau dann, wenn es passiert, mit echter Zeit zu reagieren, bevor sie tagelang unbeantwortet sichtbar bleibt.',
    email: {
      bandeja: 'Posteingang',
      ahora: 'jetzt',
      de: 'MyTapstar',
      remite: 'alertas@mytapstar.com',
      asunto: 'Neue 2★-Bewertung für dein Unternehmen',
      extracto: '„Das Essen war in Ordnung, aber es hat ziemlich lange gedauert, bis uns jemand bedient hat, und niemand …“',
      sello: '4 Min. später erhalten',
    },
  },

  porQue: {
    kicker: 'Warum das zählt',
    cifra: '9-15 %',
    cifraPie: 'der Conversion bei Neukunden gehen durch eine unbeantwortete schlechte Bewertung verloren',
    titulo: 'Eine unbeantwortete schlechte Bewertung kostet mehr, als es aussieht',
    p1: 'Eine negative Bewertung ohne Antwort bleibt nicht liegen: Sie steht weiter da, sichtbar, während jede Person, die sie liest, dir ein Stück weniger vertraut. Dieser Effekt hat messbare Kosten, zwischen 9 % und 15 % der Conversion bei Neukunden, und eine Antwort innerhalb einer Stunde kann fast den ganzen Schaden umkehren.',
    p2: 'Das ist nicht nur Höflichkeit. Das ist der Unterschied zwischen einer einzelnen Beschwerde und dem Grund, warum jemand die Konkurrenz wählt, ohne es dir zu sagen.',
  },

  faqTitulo: 'Häufige Fragen',
  faq: [
    {
      q: 'Werden die KI-Antworten von allein veröffentlicht, oder prüfe ich sie vorher?',
      a: 'Sie werden automatisch veröffentlicht, nach der Verzögerung, die du eingestellt hast. Für die Bewertungen, die du automatisierst, gibt es keinen Zwischenschritt zur manuellen Prüfung.',
    },
    {
      q: 'Kann ich den Ton der automatischen Antworten wählen?',
      a: 'Ja. Töne werden einmal angelegt und wiederverwendet: Jeder sagt, für welche Standorte er gilt — für alle oder die, die du wählst — und ob er für gute oder für kritische Bewertungen ist. Die Stimme, mit der du dich für 5 Sterne bedankst, muss also nicht dieselbe sein, mit der du auf 1 Stern antwortest.',
    },
    {
      q: 'Ich habe mehrere Standorte — richte ich die Regeln einmal ein oder für jeden?',
      a: 'Jeder Standort kann seine eigene Konfiguration haben, und es gibt außerdem eine allgemeine Regel, die alle auf einmal abdeckt, wenn du nicht einzeln vorgehen willst.',
    },
    {
      q: 'Wie viele Stichwörter kann ich gleichzeitig überwachen?',
      a: 'Bis zu 20 pro Regel. Da du eine allgemeine Regel und eine pro Standort haben kannst, reicht das in der Praxis mehr als aus für das, was du wirklich im Blick behalten willst.',
    },
    {
      q: 'Was passiert, wenn ich für dieselbe Bewertung sowohl automatisch antworten als auch einen Hinweis bekommen will?',
      a: 'Beides greift: Die Bewertung wird automatisch beantwortet und du bekommst zusätzlich die Hinweis-E-Mail. So entgeht dir der Hinweis nicht, obwohl die Antwort schon von allein raus ist.',
    },
  ],

  cierre: {
    titulo: 'Hör auf, Bewertungen hinterherzulaufen — ',
    acento: 'lass ihnen für dich hinterherlaufen',
    sub: 'Richte die Regeln einmal ein, und jede Bewertung wird von allein beantwortet, überwacht oder gemeldet, ohne dass du daran denken musst, jeden Tag reinzuschauen.',
  },
};

const publicacionesIaDe = {
  hero: {
    badge: 'Wann hast du zuletzt etwas in deinem Google-Profil veröffentlicht?',
    titulo: 'Regelmäßig zu posten ',
    acento: 'bringt dich weiter nach oben',
    tituloB: ' bei Google Maps',
    sub: 'Schreib die Idee, die KI verfasst den Beitrag, und er plant sich von allein ein — auch wenn du mehrere Standorte hast.',
    cta: '7 Tage kostenlos testen',
    ctaVer: 'Ein echtes Beispiel ansehen',
    preview: {
      etiqueta: 'Vorschau',
      tipo: 'Angebot',
      texto: 'Wir freuen uns riesig, eine großartige Aktion anzukündigen: 2 für 1 auf Burger! Komm vorbei und genieße das beste Fleisch der Stadt 🍔🔥',
      boton: 'Reservieren',
    },
  },

  problema: {
    titulo: 'Dein Google-Profil hat wahrscheinlich seit Monaten nichts gesagt',
    p1: 'Die meisten Unternehmen haben nie etwas in ihrem Google-Profil veröffentlicht. Und wer es einmal probiert hat, hat es meist sofort gelassen: Text schreiben, Foto suchen, hochladen, Button wählen — für einen einzigen Beitrag lohnt sich die Zeit nicht.',
    p2: 'Das Problem ist, dass Google Profile mit mehr Sichtbarkeit belohnt, die regelmäßig posten. Dieses Schweigen ist also nicht nur ein Profil, das leer wirkt, es ist ein weiteres Signal, das deinem Ranking fehlt — genau wie bei unbeantworteten Bewertungen.',
  },

  crear: {
    kicker: 'Beiträge mit KI erstellen',
    titulo: 'Schreib die Idee, die KI macht den Rest',
    cuerpo: 'Sag ihr in zwei Zeilen, was du veröffentlichen willst, häng ein Foto an, wenn du eins hast, und die KI schreibt den kompletten Text, bereit zum Prüfen und Veröffentlichen.',
    tuLado: 'Was du schreibst',
    suLado: 'Was du zurückbekommst',
    prompt: {
      etiqueta: 'Deine Idee',
      texto: '2 für 1 auf den Signature-Burger, nur diesen Donnerstag',
      tipoEtiqueta: 'Typ',
      tipo: 'Angebot',
      botonEtiqueta: 'Button',
      boton: 'Reservieren',
      generar: 'Mit KI erzeugen',
    },
    salida: {
      texto: '🍔 2 für 1 auf unseren Signature-Burger! Doppeltes gegrilltes Angus-Rind, geschmolzener Käse, Haussauce und frisch gebackenes Brioche, dazu handgeschnittene Premium-Pommes. 🔥 Nur diesen Donnerstag gültig, den ganzen Tag. Wir warten auf dich in der Hauptstraße 14 📍 Bring mit, wen du willst, und jeder bestellt seinen eigenen. 😋',
      boton: 'Reservieren',
    },
  },

  tipos: {
    titulo: 'Jeder Beitrag mit seinem Typ und seinem Button',
    cuerpo: 'Du wählst, ob es ein Update, ein Angebot oder eine Veranstaltung ist, und welcher Button darunter steht: mehr erfahren, reservieren, bestellen, kaufen, anmelden, anrufen oder keiner.',
    lista: [
      { clave: 'standard', n: 'Update', d: 'Neues aus dem Alltag', color: '#1a73e8' },
      { clave: 'offer', n: 'Angebot', d: 'Aktionen mit Datum', color: '#feae2c' },
      { clave: 'event', n: 'Veranstaltung', d: 'Etwas, das an einem bestimmten Tag stattfindet', color: '#7c3aed' },
    ],
    botonesEtiqueta: 'Verfügbare Buttons',
    botones: ['Mehr erfahren', 'Reservieren', 'Bestellen', 'Kaufen', 'Anmelden', 'Anrufen', 'Kein Button'],
  },

  programar: {
    titulo: 'Heute planen, veröffentlichen, wann du sagst',
    cuerpo: 'Du wählst Datum und Uhrzeit, oder lässt es sofort raus, und es wird von allein veröffentlicht. Jeder Beitrag bleibt mit seinem Status gespeichert — geplant, veröffentlicht oder fehlgeschlagen — und mit einem direkten Link zu seinen Kennzahlen bei Google.',
    diasSemana: ['M', 'D', 'M', 'D', 'F', 'S', 'S'],
    recientes: [
      { clave: 'p1', t: '2 für 1 auf Burger', color: '#feae2c', estado: 'Veröffentlicht', tono: 'bien' },
      { clave: 'p2', t: 'Neue Saisonkarte', color: '#1a73e8', estado: 'Geplant', tono: 'medio' },
    ],
  },

  multi: {
    kicker: 'Mehrere Standorte',
    titulo: 'Ein Beitrag, für jeden Standort personalisiert',
    cuerpo: 'Wenn du mehrere Standorte hast, schreibst du den Beitrag einmal mit Variablen wie dem Namen des Unternehmens, der Stadt, der Adresse oder der Telefonnummer, und er wird an jedem Standort automatisch angepasst veröffentlicht. Kein Wiederholen derselben Arbeit Standort für Standort.',
    variablesEtiqueta: 'Verfügbare Variablen',
    variables: [
      { k: '{{name}}', d: 'Name des Unternehmens' },
      { k: '{{city}}', d: 'Stadt' },
      { k: '{{address}}', d: 'Adresse' },
      { k: '{{phone}}', d: 'Telefon' },
      { k: '{{website}}', d: 'Website' },
    ],
  },

  porQue: {
    kicker: 'Warum das zählt',
    titulo: 'Google behandelt die, die posten, und die, die nicht posten, nicht gleich',
    p1: 'Du musst nicht jeden Tag posten. Aber Profile, die es mit einer gewissen Regelmäßigkeit tun, erscheinen bei Maps weiter oben als die, die seit Monaten schweigen — bei sonst gleichen Faktoren.',
    p2: 'Es ist eines der am leichtesten zu holenden Ranking-Signale, und eines der am wenigsten genutzten.',
  },

  faqTitulo: 'Häufige Fragen',
  faq: [
    {
      q: 'Muss ich immer ein Foto hochladen?',
      a: 'Nein, das ist optional. Wenn du keines anhängst, geht der Beitrag nur mit Text raus.',
    },
    {
      q: 'Kann ich vor dem Veröffentlichen ändern, was die KI schreibt?',
      a: 'Ja, der erzeugte Text ist vollständig bearbeitbar. Es wird nichts veröffentlicht, ohne dass du es vorher siehst.',
    },
    {
      q: 'Welche Beitragstypen gibt es?',
      a: 'Update, Angebot und Veranstaltung, jeder mit seiner eigenen Farbe im Kalender.',
    },
    {
      q: 'Wie weit im Voraus kann ich einen Beitrag planen?',
      a: 'Bis zu 180 Tage. Genauso lange bewahren wir das Foto auf, das du anhängst — darüber hinaus ginge der Beitrag ohne Bild raus.',
    },
    {
      q: 'Ich habe mehrere Standorte — muss ich für jeden einen eigenen Beitrag schreiben?',
      a: 'Nicht, wenn du Variablen nutzt: Du schreibst einmal und jeder Standort bekommt seine angepasste Fassung automatisch.',
    },
  ],

  cierre: {
    titulo: 'Hör auf, dein ',
    acento: 'Google-Profil',
    sub: 'Schreib die Idee, die KI macht einen Beitrag daraus, und du entscheidest, wann er rausgeht.',
  },
};

const widgetResenasDe = {
  hero: {
    badge: 'Deine Google-Bewertungen, jetzt auf deiner eigenen Website',
    titulo: 'Zeig das Vertrauen, das du ',
    acento: 'dir schon verdient hast',
    sub: 'Kopier einen Code, füg ihn auf deiner Website ein, und deine besten Google-Bewertungen fangen an, für dich zu arbeiten — bei allen, die dich noch nicht kennen.',
    cta: '7 Tage kostenlos testen',
    ctaVer: 'Beide Formate ansehen',
  },

  formatos: {
    kicker: 'Zwei Formate',
    resumen: {
      etiqueta: 'Zusammenfassung',
      titulo: 'Das Vertrauenssiegel in fünf Sekunden',
      cuerpo: 'Deine Durchschnittsnote, die Zahl der Bewertungen und das Google-Logo, alles in einer kompakten Karte. Es ist das Erste, was bestätigt, dass man dir trauen kann — bevor irgendjemand etwas lesen muss.',
      negocio: 'Dein Unternehmen',
      nota: '4,7',
      total: '1.284 Bewertungen',
    },
    carrusel: {
      etiqueta: 'Karussell',
      titulo: 'Die echten Stimmen deiner Kunden, in Bewegung',
      cuerpo: 'Deine neuesten Bewertungen, mit Foto, Namen und Datum, die von allein über deine Website laufen. Es aktualisiert sich bei jeder neuen Bewertung, ohne dass du etwas anfassen musst.',
      resenas: [
        {
          clave: 'laura', autor: 'Laura M.', inicial: 'L', fecha: '18/09/2026', estrellas: 5,
          texto: 'Alles super, die Bedienung nicht zu übertreffen und das Essen köstlich. Wir kommen ganz sicher wieder.',
        },
        {
          clave: 'diego', autor: 'Diego R.', inicial: 'D', fecha: '15/09/2026', estrellas: 4,
          texto: 'Sehr gutes Preis-Leistungs-Verhältnis, wir kommen bestimmt wieder.',
        },
        {
          clave: 'marc', autor: 'Marc T.', inicial: 'M', fecha: '11/09/2026', estrellas: 5,
          texto: 'Sehr angenehmer Ort und äußerst aufmerksames Personal. Sehr zu empfehlen.',
        },
      ],
    },
  },

  personalizar: {
    titulo: 'Etwas, das zu deiner Website passt, statt sie zu unterbrechen',
    cuerpo: 'Du wählst das Design (hell, dunkel oder das des Besuchers), die Sprache und eine Akzentfarbe, die standardmäßig schon deine Markenfarbe ist. Du stellst sogar ein, wie rund die Ecken sind. Und im Karussell entscheidest du, ob Profilfotos zu sehen sind, ob Datumsangaben erscheinen oder ob du nur 5-Sterne-Bewertungen zeigen willst.',
    temaEtiqueta: 'Design',
    temas: ['Hell', 'Dunkel'],
    colorEtiqueta: 'Akzentfarbe',
    colorNota: 'Deine Markenfarbe',
    redondeoEtiqueta: 'Eckenrundung',
    redondeoValor: '14 px',
    soloCarrusel: 'Nur im Karussell',
    opciones: [
      { clave: 'avatares', n: 'Profilfotos', on: true },
      { clave: 'fechas', n: 'Datum der Bewertung', on: true },
      { clave: 'cinco', n: 'Nur 5-Sterne-Bewertungen', on: false },
    ],
  },

  dominios: {
    titulo: 'Niemand kann es auf seine eigene Website kopieren',
    cuerpo: 'Das Widget funktioniert nur auf den Domains, die du freigibst. Solange du keine hinzufügst, erscheint es auf keiner fremden Website: nur in der Vorschau in deinem Dashboard, damit du es vor dem Veröffentlichen nach deinem Geschmack einstellen kannst.',
    etiqueta: 'Erlaubte Domains',
    ejemplo: 'deinunternehmen.de',
    activo: 'Aktiv',
    anadir: 'Weitere Domain hinzufügen…',
    anadirCta: 'Hinzufügen',
  },

  porQue: {
    kicker: 'Warum das zählt',
    cifra: '88 %',
    cifraPie: 'vertrauen einer Bewertung fast so sehr wie einem Freund',
    titulo: '88 % vertrauen einer Bewertung fast so sehr wie der Empfehlung eines Freundes',
    p1: 'Dieses Vertrauen ist viel mehr wert, wenn die Bewertung dort steht, wo der Kunde ohnehin hinschaut — auf deiner eigenen Website, nicht versteckt in einem Google-Profil, das er selbst suchen muss.',
  },

  faqTitulo: 'Häufige Fragen',
  faq: [
    {
      q: 'Muss ich programmieren können, um es einzubauen?',
      a: 'Nein, du kopierst zwei Zeilen Code und fügst sie auf deiner Website ein, wie bei jedem anderen Widget.',
    },
    {
      q: 'Macht es meine Website langsamer?',
      a: 'Nein, der Code lädt asynchron und blockiert den Rest der Seite nicht.',
    },
    {
      q: 'Funktioniert es auf jeder Plattform?',
      a: 'Ja, auf jeder Website, in die du HTML einfügen kannst: WordPress, Shopify oder eine maßgeschneiderte Seite.',
    },
    {
      q: 'Aktualisiert es sich von allein, wenn eine neue Bewertung kommt?',
      a: 'Ja, du musst den Code nicht neu erzeugen und nichts anfassen.',
    },
    {
      q: 'Kann ich Zusammenfassung und Karussell gleichzeitig nutzen?',
      a: 'Ja, auf derselben Seite oder auf verschiedenen Seiten deiner Website.',
    },
  ],

  cierre: {
    titulo: 'Stell deine Bewertungen dorthin, wo deine Kunden ',
    acento: 'ohnehin schon hinschauen',
    sub: 'Nutz die Zusammenfassung, das Karussell oder beides auf derselben Seite: die Zusammenfassung als erster Blick, das Karussell direkt darunter für alle, die mehr lesen wollen. Code kopieren und fertig.',
  },
};

const comunDe = {
  cierre: {
    cta: '7 Tage kostenlos testen',
    microcopy: '7 Tage kostenlos · unverbindlich · jederzeit kündbar',
    enterpriseA: 'Mehr als 30 Standorte? ',
    enterpriseCta: 'Vertrieb kontaktieren',
  },

  estrellasEtiqueta: '{n} von 5 Sternen',
};

const visibilidadSeoFr = {
  hero: {
    badge: 'Savez-vous à quelle position vous apparaissez quand vos clients vous cherchent ?',
    titulo: 'Dominez votre zone sur ',
    acento: 'Google Maps',
    sub: 'Attirez plus de clients en apparaissant mieux placé, et pas seulement devant votre porte.',
    cta: 'Essayer 7 jours gratuits',
    ctaVer: 'Voir le scanner en action',
    consulta: '« restaurant de sushis près de moi »',
    etiquetaZona: 'Votre zone',
  },

  problema: {
    pinCerca: 'Dans votre rue',
    pinLejos: 'À 800 m',
    titulo: 'Être sur Google Maps n’est pas la même chose qu’apparaître sur Google Maps',
    p1: 'Si vous cherchez votre propre établissement, ou si vous cherchez depuis votre établissement, vous apparaîtrez presque toujours bien. Mais un vrai client ne vous cherche pas par votre nom : il cherche quelque chose comme « restaurant de sushis près de moi » depuis un autre point de votre zone, sans savoir encore que vous existez.',
    p2: 'C’est là que votre position change. Vous pouvez être premier dans votre rue et ne pas apparaître même en deuxième page à quelques centaines de mètres. C’est un angle mort que presque personne ne détecte, parce que la façon normale de le vérifier — se chercher soi-même — est justement celle qui ne le révèle jamais.',
  },

  escaneo: {
    kicker: 'Analyse de position',
    titulo: 'Voici comment Google vous voit depuis chaque point de votre zone',
    cuerpo: 'Vous choisissez ce que cherchent vos clients (par exemple « restaurant de sushis » ou « sushis près de moi ») et la taille de la zone autour de votre établissement. En quelques secondes, vous avez une carte avec votre position exacte à chaque point : vert si vous apparaissez parmi les premiers, rouge si vous peinez à apparaître, gris si vous n’apparaissez pas du tout.',
    captura: {
      ...CAPTURA_MAPA,
      alt: 'La carte SEO du tableau de bord : à gauche le formulaire d’analyse avec l’établissement choisi et les résultats, à droite la grille des positions sur la carte de la ville.',
    },
    kpis: [
      {
        clave: 'media',
        l: 'Position moyenne',
        v: '7,9',
        d: 'Votre position moyenne, en ne comptant que les points où vous apparaissez',
      },
      {
        clave: 'cobertura',
        l: 'Couverture',
        v: '89,8 %',
        d: 'À combien des points analysés vous apparaissez, même à une place basse',
      },
      {
        clave: 'dominancia',
        l: 'Domination locale',
        v: '20,4 %',
        d: 'À quel pourcentage de points vous êtes dans les 3 premiers',
      },
    ],
  },

  competidores: {
    kicker: 'Concurrents',
    titulo: 'Comparez-vous à celui qui se bat vraiment pour vos clients',
    cuerpo: 'Vous choisissez le concurrent par son nom : l’établissement même qui s’affiche à vos clients quand ils cherchent ce que vous vendez. Vous le sélectionnez dans la liste des établissements de votre zone, avec sa note et ses avis sous les yeux, et vous comparez sa position à la vôtre, point par point. Vous voyez ainsi une chose qu’une moyenne ne vous dirait jamais : vous pouvez être bien placé dans votre rue et, à quelques centaines de mètres, il apparaît avant vous.',
  },

  analisis: {
    kicker: 'Analyse SEO',
    titulo: 'Vous savez exactement ce qui vous freine, pas seulement que ça va mal',
    p1: 'Votre fiche reçoit une note de 0 à 100 qui résume à quel point elle est prête à bien se positionner, avec des niveaux qui vont de « Au démarrage » à « Leader local ».',
    p2: 'Mais le chiffre en lui-même ne change rien. Ce qui compte, c’est que derrière cette note nous savons exactement ce qui pèse : s’il vous manque des avis récents, si votre fiche n’a rien publié depuis des mois, si les catégories ne collent pas à ce que les gens cherchent, ou s’il vous manque simplement des photos à jour.',
    p3: 'Ce n’est ni un avis ni une boîte noire : cela vient des données réelles de votre fiche. C’est pourquoi, quand vous changez quelque chose, la note bouge vraiment, et pas selon ce que nous en disons.',
    p4: 'Et plutôt que de vous laisser avec une liste de vingt choses à revoir, nous vous disons laquelle vous fera le plus avancer en premier. Vous commencez par là, pas là où ça vous semble.',

    tarjeta: {
      puntuacion: '62',
      sobre: '/ 100',
      nivelEtiqueta: 'Niveau actuel',
      nivel: 'En route vers le podium',
      faltan: 'Il vous reste 9 points pour atteindre « Bien positionné ».',
      metricas: [
        { clave: 'sinResponder', l: 'Avis sans réponse', v: '4', tono: 'mal' },
        { clave: 'tasa', l: 'Taux de réponse (30 jours)', v: '62 % · objectif 80 %', tono: 'medio' },
        { clave: 'evolucion', l: 'Évolution ce mois-ci', v: '+6 pts', tono: 'bien' },
      ],
      mision: {
        etiqueta: 'Prochaine mission',
        texto: 'Répondez aux avis en moins de 24 heures',
        puntos: '+8 pts une fois faite',
      },
      categorias: [
        { clave: 'nap', n: 'Informations NAP', p: 20, t: 20 },
        { clave: 'cat', n: 'Catégories', p: 16, t: 20 },
        { clave: 'vis', n: 'Présence visuelle', p: 12, t: 18 },
        { clave: 'rep', n: 'Réputation', p: 8, t: 20 },
        { clave: 'srv', n: 'Mots-clés et services', p: 6, t: 12 },
        { clave: 'act', n: 'Activité', p: 0, t: 10 },
      ],
    },
  },

  porQue: {
    kicker: 'Pourquoi c’est important',
    cifra: '40-50 %',
    cifraPie: 'des clics de la recherche vont à la carte des trois premiers',
    titulo: 'Le premier résultat ne prend pas seulement le premier clic, il en prend la moitié',
    p1: 'Quand quelqu’un cherche un établissement comme le vôtre, il n’ouvre presque jamais un site. Il voit la carte avec les trois premiers résultats (le « pack local ») et décide sur place.',
    p2: 'Cette carte emporte entre 40 et 50 % de tous les clics de la recherche. Si vous apparaissez en quatrième position ou plus bas, il ne vous reste qu’une fraction minime de ce trafic, même si votre établissement est meilleur que les trois du dessus.',
    p3: 'Ce n’est pas une petite marge que l’on peut ignorer. C’est la différence entre être trouvé et ne pas être vu du tout.',
  },

  faqTitulo: 'Questions fréquentes',
  faq: [
    {
      q: 'Faut-il des connaissances techniques pour l’utiliser ?',
      a: 'Non. Vous connectez votre fiche Google Business en deux minutes et vous avez votre première analyse dans la même session.',
    },
    {
      q: 'Combien de points sont analysés, et de quoi cela dépend-il ?',
      a: 'Vous choisissez la taille de la grille autour de votre établissement, d’une petite zone à une large couverture. Plus vous couvrez de zone, plus vous analysez de points : une grille 5×5 fait 25 points et une 7×7 en fait 49. Chaque point coûte un crédit, et le solde qu’il vous reste est toujours visible sur l’écran du scanner.',
    },
    {
      q: 'Puis-je comparer avec plus d’un concurrent à la fois ?',
      a: 'Vous voyez la liste complète des établissements de votre catégorie dans la zone, avec leur position moyenne et leur domination locale. Vous comparez avec un seul à la fois, mais vous pouvez changer de concurrent quand vous voulez sans dépenser une nouvelle analyse.',
    },
    {
      q: 'J’ai plusieurs établissements, est-ce que cela fonctionne pareil ?',
      a: 'Oui. Chaque établissement a sa propre grille et sa propre note, et vous pouvez voir un classement comparatif entre tous vos établissements pour savoir lequel a le plus besoin d’attention.',
    },
    {
      q: 'La note de l’analyse SEO se met-elle à jour toute seule, ou faut-il relancer une analyse ?',
      a: 'Elle se met à jour toute seule. Elle n’est pas liée aux analyses de la carte SEO : elle ne consomme donc pas de crédits et ne dépend pas d’une nouvelle analyse. Elle est recalculée chaque fois que vous ouvrez l’écran, à partir des données de votre fiche (avis, photos, catégories, publications). Un changement que vous venez de faire dans Google peut mettre un peu de temps à apparaître, le temps que nous relisions votre fiche.',
    },
  ],

  cierre: {
    titulo: 'Arrêtez de deviner comment vous voit ',
    acento: 'Google',
    sub: 'Connectez votre fiche en deux minutes, analysez votre zone et sachez dans la même session où vous en êtes et quoi faire pour monter.',
  },
};

const automatizacionesFr = {
  hero: {
    titulo: 'Répondre à temps vous ',
    acento: 'place plus haut',
    tituloB: ' sur Google Maps',
    sub: 'Google valorise l’activité de votre profil, et un profil qui répond à ses avis se place plus haut. Ici, les réponses partent toutes seules, avec votre ton et au bon moment.',
    cta: 'Essayer 7 jours gratuits',
    ctaVer: 'Voir des exemples de réponse',
  },

  problema: {
    pendientes: '4 sans réponse',
    titulo: 'Chaque avis auquel vous ne répondez pas à temps vous coûte aussi des positions sur Google Maps',
    p1: 'Entre la gestion de l’établissement et tout le reste, répondre à chaque avis un par un finit toujours par attendre. Il n’y a pas de minutes en trop dans la journée pour ça.',
    p2: 'Mais Google se sert du nombre d’avis auxquels vous répondez comme signal pour votre position dans les recherches locales. Chaque avis sans réponse ne reste donc pas seulement en attente : il vous retire aussi des points là où vous jouez le plus gros, là où vous cherche quelqu’un qui ne vous connaît pas encore.',
  },

  reglas: {
    kicker: 'Réponses avec IA',
    quien: {
      titulo: 'C’est vous qui décidez quels avis reçoivent une réponse automatique',
      cuerpo: 'Vous choisissez quelles notes reçoivent une réponse automatique, de 5 étoiles à 1, sans restriction. Chaque réponse est rédigée par l’IA sur le moment, adaptée à cet avis précis, et toujours avec le ton que vous employez déjà sur votre fiche.',
      estados: { auto: 'Réponse automatique', aviso: 'Alerte seulement' },
    },
    cuando: {
      titulo: 'Ni à la seconde, ni le lendemain',
      cuerpo: 'Vous fixez un temps d’attente avant la publication de chaque réponse, ou vous le laissez varier dans une fourchette, par exemple entre 15 minutes et une heure. Une réponse en cinq secondes a l’air d’un robot ; une réponse après un délai raisonnable a l’air de quelqu’un qui a vraiment lu ce que vous avez écrit. Et vous restez dans la fenêtre qui vous intéresse le plus : répondre vite est un signal dont Google tient compte.',
      min: 'Tout de suite',
      max: '24 h',
      publicaEntre: 'Publie entre',
      y: 'et',
      desde: '15 min',
      hasta: '1 h',
    },
  },

  ejemplos: {
    titulo: 'Toutes les réponses ne se valent pas',
    cuerpo: 'Il y a une vraie différence entre ce que vous écririez à la va-vite entre deux clients et ce que reçoit chaque avis quand l’IA s’en charge.',
    resena: {
      autor: 'Laura M.',
      inicial: 'L',
      estrellas: 5,
      texto: 'Les sushis étaient excellents et le service rapide.',
    },
    aMano: {
      etiqueta: 'À la main, vite fait',
      texto: '« Merci pour votre avis ! Nous sommes ravis que vous ayez passé un bon moment. À bientôt ! »',
    },
    conIa: {
      etiqueta: 'Avec l’IA',
      texto: '« Merci pour vos mots, nous sommes vraiment ravis que les sushis vous aient plu et que le service ait été rapide, c’est un point auquel nous tenons beaucoup. Nous vous attendons bientôt pour goûter une nouveauté de la carte. »',
    },
    remate: 'La réponse rapide fait l’affaire. Celle de l’IA montre qu’il y a quelqu’un derrière, exactement ce qu’un nouveau client remarque en comparant votre fiche avec celle d’à côté.',
  },

  alertaPalabra: {
    kicker: 'Alerte par mot-clé',
    titulo: 'Il y a des mots que vous devez voir tout de suite, pas en relisant les avis du mois',
    p1: 'Vous choisissez les mots qui comptent pour vous, et dès qu’un nouvel avis les mentionne, vous recevez un e-mail immédiatement.',
    p2: 'C’est utile pour l’urgent, comme apprendre sur le moment que quelqu’un mentionne quelque chose de grave. Et pour l’inattendu aussi : si plusieurs clients répètent « végan » ou « sans gluten » alors que vous ne le proposez pas, c’est de la demande qui vous échappe.',
    urgente: { etiqueta: 'Surveillez l’urgent', chips: ['cafard', 'intoxication', 'Sushi Kanda'] },
    oportunidad: { etiqueta: 'Repérez l’opportunité', chips: ['végan', 'sans gluten', 'terrasse'] },
  },

  alertaNota: {
    kicker: 'Alerte par note basse',
    titulo: 'Apprenez le mauvais avis en quelques minutes, pas une fois que tout le monde l’a lu',
    cuerpo: 'Vous choisissez les notes à surveiller, de 1 à 3 étoiles, et dès qu’un avis arrive avec cette note, vous êtes prévenu par e-mail sur le moment. Pas besoin de vérifier le tableau de bord au cas où : vous l’apprenez juste quand cela arrive, avec du vrai temps pour réagir avant qu’il ne reste visible des jours sans réponse.',
    email: {
      bandeja: 'Boîte de réception',
      ahora: 'maintenant',
      de: 'MyTapstar',
      remite: 'alertas@mytapstar.com',
      asunto: 'Nouvel avis 2★ sur votre établissement',
      extracto: '« La cuisine était bien, mais ils ont mis un bon moment à nous servir et personne… »',
      sello: 'Reçu 4 min plus tard',
    },
  },

  porQue: {
    kicker: 'Pourquoi c’est important',
    cifra: '9-15 %',
    cifraPie: 'de conversion de nouveaux clients se perdent avec un mauvais avis sans réponse',
    titulo: 'Un mauvais avis sans réponse coûte plus cher qu’il n’y paraît',
    p1: 'Un avis négatif sans réponse ne reste pas inerte : il est toujours là, visible, pendant que chaque personne qui le lit vous fait un peu moins confiance. Cet effet a un coût mesurable, entre 9 % et 15 % de conversion de nouveaux clients, et une réponse en moins d’une heure peut réparer presque tous les dégâts.',
    p2: 'Ce n’est pas qu’une question de politesse. C’est la différence entre une plainte isolée et la raison pour laquelle quelqu’un choisit votre concurrent sans vous le dire.',
  },

  faqTitulo: 'Questions fréquentes',
  faq: [
    {
      q: 'Les réponses de l’IA sont-elles publiées toutes seules, ou est-ce que je les relis avant ?',
      a: 'Elles sont publiées automatiquement, selon le délai que vous avez configuré. Il n’y a pas d’étape intermédiaire de relecture manuelle pour les notes que vous décidez d’automatiser.',
    },
    {
      q: 'Puis-je choisir le ton des réponses automatiques ?',
      a: 'Oui. Les tons se définissent une fois et se réutilisent : chacun indique à quels établissements il s’applique — tous ou ceux que vous choisissez — et s’il est destiné aux bons avis ou aux critiques. La voix avec laquelle vous remerciez un 5 étoiles n’est donc pas forcément celle avec laquelle vous répondez à un 1.',
    },
    {
      q: 'J’ai plusieurs établissements, dois-je configurer les règles une fois ou pour chacun ?',
      a: 'Chaque établissement peut avoir sa propre configuration, et il existe en plus une règle générale qui les couvre tous à la fois si vous préférez ne pas y aller un par un.',
    },
    {
      q: 'Combien de mots-clés puis-je surveiller à la fois ?',
      a: 'Jusqu’à 20 par règle. Comme vous pouvez avoir une règle générale et une autre par établissement, cela suffit largement en pratique pour couvrir ce que vous voulez vraiment surveiller.',
    },
    {
      q: 'Que se passe-t-il si je choisis à la fois de répondre automatiquement et de recevoir une alerte pour la même note ?',
      a: 'Les deux se déclenchent : l’avis reçoit une réponse automatique et, en plus, vous recevez l’e-mail d’alerte. Vous ne ratez donc pas l’avertissement même si la réponse est déjà partie toute seule.',
    },
  ],

  cierre: {
    titulo: 'Arrêtez de courir après les avis, ',
    acento: 'qu’on leur coure après pour vous',
    sub: 'Configurez les règles une fois et chaque avis reçoit une réponse, est surveillé ou vous alerte tout seul, sans que vous ayez à penser à vous connecter chaque jour.',
  },
};

const publicacionesIaFr = {
  hero: {
    badge: 'À quand remonte votre dernière publication sur votre fiche Google ?',
    titulo: 'Publier régulièrement vous ',
    acento: 'place plus haut',
    tituloB: ' sur Google Maps',
    sub: 'Écrivez l’idée, l’IA rédige la publication, et elle se programme toute seule même si vous avez plusieurs établissements.',
    cta: 'Essayer 7 jours gratuits',
    ctaVer: 'Voir un exemple réel',
    preview: {
      etiqueta: 'Aperçu',
      tipo: 'Offre',
      texto: 'Nous sommes ravis d’annoncer une promo incroyable : 2 pour 1 sur les burgers ! Venez nous voir et profitez de la meilleure viande de la ville 🍔🔥',
      boton: 'Réserver',
    },
  },

  problema: {
    titulo: 'Votre fiche Google n’a probablement rien dit depuis des mois',
    p1: 'La plupart des établissements n’ont jamais rien publié sur leur fiche Google. Et ceux qui ont essayé une fois ont en général abandonné tout de suite : écrire le texte, trouver une photo, la téléverser, choisir le bouton ; pour une seule publication, le temps que ça prend ne vaut pas le coup.',
    p2: 'Le problème, c’est que Google récompense par plus de visibilité les profils qui publient régulièrement. Ce silence n’est donc pas seulement une fiche qui a l’air vide : c’est un signal de plus qui manque à votre référencement, exactement comme pour les avis sans réponse.',
  },

  crear: {
    kicker: 'Créez des publications avec l’IA',
    titulo: 'Écrivez l’idée, l’IA fait le reste',
    cuerpo: 'Dites-lui en deux lignes ce que vous voulez publier, joignez une photo si vous en avez une, et l’IA écrit le texte complet, prêt à relire et à publier.',
    tuLado: 'Ce que vous écrivez',
    suLado: 'Ce qu’elle vous renvoie',
    prompt: {
      etiqueta: 'Votre idée',
      texto: '2 pour 1 sur le burger signature, ce jeudi seulement',
      tipoEtiqueta: 'Type',
      tipo: 'Offre',
      botonEtiqueta: 'Bouton',
      boton: 'Réserver',
      generar: 'Générer avec l’IA',
    },
    salida: {
      texto: '🍔 2 pour 1 sur notre burger signature ! Double steak d’Angus grillé, fromage fondu, sauce maison et pain brioché tout juste sorti du four, avec des frites premium coupées à la main. 🔥 Offre valable ce jeudi uniquement, toute la journée. On vous attend au 14 rue Principale 📍 Venez avec qui vous voulez et que chacun prenne le sien. 😋',
      boton: 'Réserver',
    },
  },

  tipos: {
    titulo: 'Chaque publication avec son type et son bouton',
    cuerpo: 'Vous choisissez s’il s’agit d’une actualité, d’une offre ou d’un événement, et quel bouton figure en dessous : en savoir plus, réserver, commander, acheter, s’inscrire, appeler, ou aucun.',
    lista: [
      { clave: 'standard', n: 'Actualité', d: 'Les nouvelles du quotidien', color: '#1a73e8' },
      { clave: 'offer', n: 'Offre', d: 'Promotions avec une date', color: '#feae2c' },
      { clave: 'event', n: 'Événement', d: 'Quelque chose qui a lieu un jour précis', color: '#7c3aed' },
    ],
    botonesEtiqueta: 'Boutons disponibles',
    botones: ['En savoir plus', 'Réserver', 'Commander', 'Acheter', 'S’inscrire', 'Appeler', 'Sans bouton'],
  },

  programar: {
    titulo: 'Programmez aujourd’hui, publiez quand vous voulez',
    cuerpo: 'Vous choisissez la date et l’heure, ou vous la laissez pour tout de suite, et elle se publie toute seule. Chaque publication est enregistrée avec son statut — programmée, publiée ou échouée — et un lien direct vers ses statistiques dans Google.',
    diasSemana: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
    recientes: [
      { clave: 'p1', t: '2 pour 1 sur les burgers', color: '#feae2c', estado: 'Publiée', tono: 'bien' },
      { clave: 'p2', t: 'Nouvelle carte de saison', color: '#1a73e8', estado: 'Programmée', tono: 'medio' },
    ],
  },

  multi: {
    kicker: 'Multi-établissements',
    titulo: 'Une publication, personnalisée pour chaque site',
    cuerpo: 'Si vous avez plusieurs établissements, vous écrivez la publication une fois en utilisant des variables comme le nom de l’établissement, la ville, l’adresse ou le téléphone, et elle se publie adaptée automatiquement sur chaque site. Fini de refaire le même travail établissement par établissement.',
    variablesEtiqueta: 'Variables disponibles',
    variables: [
      { k: '{{name}}', d: 'Nom de l’établissement' },
      { k: '{{city}}', d: 'Ville' },
      { k: '{{address}}', d: 'Adresse' },
      { k: '{{phone}}', d: 'Téléphone' },
      { k: '{{website}}', d: 'Site web' },
    ],
  },

  porQue: {
    kicker: 'Pourquoi c’est important',
    titulo: 'Google ne traite pas de la même façon ceux qui publient et ceux qui ne publient pas',
    p1: 'Il n’est pas nécessaire de publier tous les jours. Mais les profils qui le font avec une certaine régularité apparaissent plus haut sur Maps que ceux qui se taisent depuis des mois, à facteurs égaux par ailleurs.',
    p2: 'C’est l’un des signaux de référencement les plus faciles à gagner, et l’un de ceux dont le moins d’établissements profitent.',
  },

  faqTitulo: 'Questions fréquentes',
  faq: [
    {
      q: 'Dois-je toujours ajouter une photo ?',
      a: 'Non, c’est facultatif. Si vous n’en joignez pas, la publication sort en texte seul.',
    },
    {
      q: 'Puis-je modifier ce qu’écrit l’IA avant de publier ?',
      a: 'Oui, le texte généré est entièrement modifiable. Rien n’est publié sans que vous l’ayez vu avant.',
    },
    {
      q: 'Quels types de publication existe-t-il ?',
      a: 'Actualité, offre et événement, chacun avec sa propre couleur dans le calendrier.',
    },
    {
      q: 'Combien de temps à l’avance puis-je programmer une publication ?',
      a: 'Jusqu’à 180 jours. C’est le même délai pendant lequel nous conservons la photo que vous joignez : au-delà, la publication partirait sans image.',
    },
    {
      q: 'J’ai plusieurs établissements, dois-je écrire une publication différente pour chacun ?',
      a: 'Pas si vous utilisez des variables : vous écrivez une fois et chaque établissement reçoit sa version adaptée automatiquement.',
    },
  ],

  cierre: {
    titulo: 'Arrêtez d’improviser votre ',
    acento: 'fiche Google',
    sub: 'Écrivez l’idée, l’IA la transforme en publication, et c’est vous qui décidez quand elle sort.',
  },
};

const widgetResenasFr = {
  hero: {
    badge: 'Vos avis Google, désormais sur votre propre site',
    titulo: 'Montrez la confiance que vous ',
    acento: 'avez déjà gagnée',
    sub: 'Copiez un code, collez-le sur votre site, et vos meilleurs avis Google se mettent à travailler pour convaincre ceux qui ne vous connaissent pas encore.',
    cta: 'Essayer 7 jours gratuits',
    ctaVer: 'Voir les deux formats',
  },

  formatos: {
    kicker: 'Deux formats',
    resumen: {
      etiqueta: 'Résumé',
      titulo: 'Le sceau de confiance en cinq secondes',
      cuerpo: 'Votre note moyenne, le nombre d’avis et le logo Google, le tout dans une carte compacte. C’est la première chose qui confirme qu’on peut vous faire confiance, avant même que quiconque ait à lire quoi que ce soit.',
      negocio: 'Votre établissement',
      nota: '4,7',
      total: '1 284 avis',
    },
    carrusel: {
      etiqueta: 'Carrousel',
      titulo: 'Les vraies voix de vos clients, en mouvement',
      cuerpo: 'Vos avis les plus récents, avec photo, nom et date, qui défilent tout seuls sur votre site. Cela se met à jour à chaque nouvel avis, sans que vous ayez à toucher à quoi que ce soit.',
      resenas: [
        {
          clave: 'laura', autor: 'Laura M.', inicial: 'L', fecha: '18/09/2026', estrellas: 5,
          texto: 'Tout était parfait, un accueil irréprochable et une cuisine excellente. Nous reviendrons, c’est sûr.',
        },
        {
          clave: 'diego', autor: 'Diego R.', inicial: 'D', fecha: '15/09/2026', estrellas: 4,
          texto: 'Très bon rapport qualité-prix, nous reviendrons sans hésiter.',
        },
        {
          clave: 'marc', autor: 'Marc T.', inicial: 'M', fecha: '11/09/2026', estrellas: 5,
          texto: 'Endroit très agréable et personnel aux petits soins. Je recommande vivement.',
        },
      ],
    },
  },

  personalizar: {
    titulo: 'Qu’il s’accorde à votre site, plutôt qu’il ne l’interrompe',
    cuerpo: 'Vous choisissez le thème (clair, sombre ou celui du visiteur), la langue, et une couleur d’accent qui part déjà sur celle de votre marque. Vous ajustez jusqu’à l’arrondi des coins. Et dans le carrousel, vous décidez si les photos de profil apparaissent, si les dates apparaissent, ou si vous ne voulez afficher que les avis 5 étoiles.',
    temaEtiqueta: 'Thème',
    temas: ['Clair', 'Sombre'],
    colorEtiqueta: 'Couleur d’accent',
    colorNota: 'Votre couleur de marque',
    redondeoEtiqueta: 'Arrondi des coins',
    redondeoValor: '14 px',
    soloCarrusel: 'Carrousel uniquement',
    opciones: [
      { clave: 'avatares', n: 'Photos de profil', on: true },
      { clave: 'fechas', n: 'Dates de l’avis', on: true },
      { clave: 'cinco', n: 'Uniquement les avis 5 étoiles', on: false },
    ],
  },

  dominios: {
    titulo: 'Personne ne peut le copier sur son propre site',
    cuerpo: 'Le widget ne fonctionne que sur les domaines que vous autorisez. Tant que vous n’en ajoutez aucun, il ne s’affiche sur aucun site tiers : uniquement dans l’aperçu de votre tableau de bord, pour que vous puissiez le régler à votre goût avant de le publier.',
    etiqueta: 'Domaines autorisés',
    ejemplo: 'votreetablissement.fr',
    activo: 'Actif',
    anadir: 'Ajouter un autre domaine…',
    anadirCta: 'Ajouter',
  },

  porQue: {
    kicker: 'Pourquoi c’est important',
    cifra: '88 %',
    cifraPie: 'font confiance à un avis presque autant qu’à un ami',
    titulo: '88 % font confiance à un avis presque autant qu’à la recommandation d’un ami',
    p1: 'Cette confiance vaut bien plus si l’avis se trouve là où le client regarde déjà, sur votre propre site, et non caché dans une fiche Google qu’il doit aller chercher lui-même.',
  },

  faqTitulo: 'Questions fréquentes',
  faq: [
    {
      q: 'Faut-il savoir programmer pour l’installer ?',
      a: 'Non, vous copiez deux lignes de code et vous les collez sur votre site, comme n’importe quel autre widget.',
    },
    {
      q: 'Est-ce que cela ralentit mon site ?',
      a: 'Non, le code se charge de façon asynchrone et ne bloque pas le reste de la page.',
    },
    {
      q: 'Est-ce que cela fonctionne sur n’importe quelle plateforme ?',
      a: 'Oui, sur tout site qui vous laisse coller du code HTML : WordPress, Shopify ou un site sur mesure.',
    },
    {
      q: 'Est-ce que cela se met à jour tout seul quand un nouvel avis arrive ?',
      a: 'Oui, pas besoin de régénérer le code ni de toucher à quoi que ce soit.',
    },
    {
      q: 'Puis-je utiliser le résumé et le carrousel en même temps ?',
      a: 'Oui, sur la même page ou sur des pages différentes de votre site.',
    },
  ],

  cierre: {
    titulo: 'Mettez vos avis là où vos clients ',
    acento: 'regardent déjà',
    sub: 'Utilisez le résumé, le carrousel, ou les deux à la fois sur la même page : le résumé comme premier coup d’œil, le carrousel juste en dessous pour qui veut en lire plus. Copiez le code et c’est en place.',
  },
};

const comunFr = {
  cierre: {
    cta: 'Essayer 7 jours gratuits',
    microcopy: '7 jours gratuits · sans engagement · annulez quand vous voulez',
    enterpriseA: 'Plus de 30 établissements ? ',
    enterpriseCta: 'Contacter les ventes',
  },

  estrellasEtiqueta: '{n} étoiles sur 5',
};

export const productoV3 = {
  es: { visibilidadSeo, automatizaciones, publicacionesIa, widgetResenas, comun },
  fr: {
    visibilidadSeo: visibilidadSeoFr,
    automatizaciones: automatizacionesFr,
    publicacionesIa: publicacionesIaFr,
    widgetResenas: widgetResenasFr,
    comun: comunFr,
  },
  de: {
    visibilidadSeo: visibilidadSeoDe,
    automatizaciones: automatizacionesDe,
    publicacionesIa: publicacionesIaDe,
    widgetResenas: widgetResenasDe,
    comun: comunDe,
  },
  pt: {
    visibilidadSeo: visibilidadSeoPt,
    automatizaciones: automatizacionesPt,
    publicacionesIa: publicacionesIaPt,
    widgetResenas: widgetResenasPt,
    comun: comunPt,
  },
  en: {
    visibilidadSeo: visibilidadSeoEn,
    automatizaciones: automatizacionesEn,
    publicacionesIa: publicacionesIaEn,
    widgetResenas: widgetResenasEn,
    comun: comunEn,
  },
};
